<template>
    <div class="order-view">
        <card :title="'Order' + (routeMode == 'verify' ? ' Verification' : '') + ' - ' + patientName">
            <template #header-right>
                <div class="d-flex align-items-center justify-content-end">
                    <b-link v-if="canPrintDeliveryLog" class="mr-3" v-b-tooltip.hover
                        :disabled="deliveryLogIsStale" :title="deliveryLogTooltip"
                        @click="openDeliveryLog">
                        Delivery Log <i class="fas fa-print" />
                    </b-link>
                    <history-modal v-model="order" class="mr-3" />
                    <router-link v-if="patient && patient.id" target="_blank"
                        :to="{ name: 'PatientDashboard', params: { id: patient.id } }">
                        <span>Patient Dashboard</span> <i class="fas fa-user"></i>
                    </router-link>
                </div>
            </template>

            <div v-if="availableAddresses?.length > 1" class="mb-3">
                <label>Address:</label>
                <b-select :disabled="order.status && order.status != orderStatus.Created"
                    :state="(order.status && order.status != orderStatus.Created) ? null : isSelectedAddressAllowed"
                    v-model="order.patientAddressId" :options="availableAddresses.map(pa => ({
                        value: pa.isPrimary ? null : pa.id,
                        text: (!(order.status && order.status != orderStatus.Created) && !isAddressAllowed(pa) ? '⛔ ':'') + pa.address1 + ' ' + (pa.address2 ?? '') + ' ' + pa.city + ' ' + pa.state + ' ' + pa.postalCode,
                        disabled: !isAddressAllowed(pa)
                    }))" />
            </div>
            <span v-else>
                {{ (patient && patient.fullAddress) || "" }}
            </span>

            <p v-show="patient && patient.signatureRequired" style="color: red">
                <br /><strong>Patient is marked Signature Required!</strong>
            </p>
            <b-row v-if="patient && patient.deceased">
                <b-col>
                    <b-alert :show="true" variant="danger">
                        <i class="fas fa-exclamation-triangle"></i>
                        <strong> Patient is deceased.</strong>
                        Orders cannot be created on patient.
                    </b-alert>
                </b-col>
            </b-row>
            <b-row v-if="routeMode == 'verify' && !pendingToBeVerified && haveSelectedPrescriptions">
                <b-col>
                    <b-alert :show="true" variant="success">
                        All prescriptions in this order were already verified.
                    </b-alert>
                </b-col>
            </b-row>
            <b-row v-if="order.status == orderStatus.Canceled">
                <b-col>
                    <b-alert :show="true" variant="danger">
                        This order is canceled.
                        <template v-if="order.cancellationReason">
                            Cancellation Reason:
                            {{ order.cancellationReason }}
                        </template>
                    </b-alert>
                </b-col>
            </b-row>
            <b-row v-if="order.wasShipped">
                <b-col>
                    <b-alert :show="true" variant="info"> This order was already shipped.</b-alert>
                </b-col>
            </b-row>
            <b-row v-else-if="isOrderVerified">
                <b-col>
                    <b-alert :show="true" variant="warning"> This order has been verified and sent to shipping.
                    </b-alert>
                </b-col>
            </b-row>
            <b-row v-if="!!availableToOrderRequirePV1?.length && !order?.id">
                <b-col>
                    <b-alert :show="true" variant="warning">
                        {{ availableToOrderRequirePV1.length === 1 ? "A prescription " : "Multiple prescriptions " }}
                        for this patient require PV1.
                    </b-alert>
                </b-col>
            </b-row>

            <ShipToPrescriberBanner :visible="isShipToPrescriber" />
            <CompletionItemsBanner
                v-if="order && order.id && (order.isPartialFill || order.isCompletionOrder)"
                :order-id="order.id"
                :is-partial-fill="!!order.isPartialFill"
                :is-completion-order="!!order.isCompletionOrder"
            />

            <b-row id="store-program-row">
                <b-col cols="6">
                    <SearchComponent :id="selectedStore && +selectedStore.id" v-model="selectedStore" label="Pharmacy"
                        search-u-r-l="/Store" prepopulate clearable :disabled="disableStoreSelection" />
                </b-col>
                <b-col cols="6">
                    <SearchComponent :id="selectedProgram && +selectedProgram.id" v-model="selectedProgram"
                        label="Program" search-u-r-l="/Program" prepopulate clearable
                        :paramsInGetById="{ includeDrugs: true }" :params="{ active: true }"
                        :disabled="disableProgramSelection" />
                </b-col>
            </b-row>
            <template v-if="routeMode == 'verify'">
                <hr />
                <!-- OPEN ORDERS FOR MERGING -->
                <card ref="openOrdersCard">
                    <b-row>
                        <b-col>
                            <h4 class="card-subtitle">Open Orders</h4>
                        </b-col>
                    </b-row>
                    <OrdersListComponent :orders="orders" expandable mergable @merge-order="patientOrderMerge"
                        @change="loadOrders" />
                </card>
            </template>
            <!-- PRESCRIPTIONS -->
            <hr />
            <b-row class="mb-3">
                <b-col>
                    <h5>Prescriptions</h5>
                    <b-table id="rx-table" ref="rxTable" :fields="availableRxFields" :items="availableToOrder"
                        :no-select-on-click="routeMode == 'verify' || routeMode == 'pickup' || isOrderCancelled"
                        :selectable="!disableFields" :tbody-tr-class="getRowClass" selected-variant="success"
                        title="Prescriptions" @row-selected="prescriptionsSelected">
                        <template #cell(rxId)="data">
                            {{ data.item.rxId }}
                            <i v-if="data.item.isAutoRefillEnrolled" class="fa fa-redo text-info ml-1"
                                v-b-tooltip.hover title="Auto-Refill Enrolled" style="font-size: 0.8em;" />
                            <b-badge v-if="data.item.removedFromPrintQueue" variant="warning" class="ml-1"
                                v-b-tooltip.hover :title="removedFromPrintQueueTooltip(data.item)">
                                Removed from print queue
                            </b-badge>
                        </template>
                        <template #cell(verifyButton)="row">
                            <b-col v-if="routeMode == 'verify'">
                                <router-link v-if="$canNavigateTo(getPrescriptionVerificationRoute(row.item))"
                                    :to="getPrescriptionVerificationRoute(row.item)">
                                    <i v-b-tooltip.hover :class="getVerifyActionButtonsClass(row.item)"
                                        :title="tooltipText(row.item)" class="fas fa-clipboard-check"
                                        style="cursor: pointer"></i>
                                </router-link>
                                <i v-else v-b-tooltip.hover :class="getVerifyActionButtonsClass(row.item)"
                                    :title="tooltipText(row.item)" class="fas fa-clipboard-check"></i>
                            </b-col>
                            <div v-else-if="routeMode == 'edit' && row.item.requiresPV1">
                                Needs PV1
                            </div>
                        </template>
                    </b-table>
                </b-col>
            </b-row>

            <!--Blocking Tasks-->
            <TasksPage v-if="patient?.id" size="sm" title="Blocking Tasks" :patient="patient"
                :prescriptions="dataPrescriptions" :blocking="true" :only-show-if-has-tasks="true" icon="fa fa-warning"
                :hide-create="true" :hide-filters="true" title-class="bg-warning p-2 rounded" :paging="false"
                :task-list-filter="blockingTaskFilter" />

            <!-- Copay Collection Flag Alert -->
            <b-alert v-if="selectedProgram" v-model="isProgramWithCopayCollectionFlag" variant="info">
                <b>No payment nor OTCs are required</b> since the order program
                <b>{{ selectedProgram.name }}</b> has flag "Copay Collection (Don't contact patient)".
            </b-alert>

            <hr />
            <!-- OTC ITEMS -->
            <b-row class="mb-3" id="otc-items-row">
                <b-col>
                    <h5 class="card-subtitle">Over the Counter</h5>
                    <b-row>
                        <b-col>
                            <SearchComponent v-if="routeMode != 'verify' && routeMode != 'pickup'"
                                :disabled="disableFields || !StoreAndRxSelected" label="Item"
                                :option-label="otcItemLabel" search-u-r-l="/OTCItem" :params="{ active: true }"
                                @input="addSelectedItem" />
                        </b-col>
                    </b-row>
                    <b-row v-if="dataOrderItems.length > 0" class="mt-5">
                        <b-col>
                            <b-table-simple fixed>
                                <b-thead head-variant="dark">
                                    <b-tr>
                                        <b-th>Item</b-th>
                                        <b-th>Quantity</b-th>
                                        <b-th>Price</b-th>
                                        <b-th>Subtotal</b-th>
                                        <b-th>Tax</b-th>
                                        <b-th>Total</b-th>
                                        <b-th></b-th>
                                    </b-tr>
                                </b-thead>
                                <b-tbody>
                                    <b-tr v-for="otcItem in dataOrderItems" :key="otcItem.otcItemId">
                                        <b-td>{{ otcItem.otcItemName }}</b-td>
                                        <b-td>
                                            <fg-input v-model.number="otcItem.quantity" :disabled="disableFields"
                                                max="100" min="0" placeholder="Quantity" size="sm" type="number"
                                                @input="updateAddonPricing(dataOrderItems)" />
                                        </b-td>
                                        <b-td>{{ otcItem.price.toFixed(2) | currency }}</b-td>
                                        <b-td>
                                            {{
                                                (parseFloat(otcItem.price.toString()) *
                                                    parseFloat(otcItem.quantity.toString()))
                                                    | currency
                                            }}
                                        </b-td>
                                        <b-td>{{ otcItem.tax | currency }}</b-td>
                                        <b-td>
                                            {{
                                                (parseFloat(otcItem.tax.toString()) +
                                                    parseFloat(otcItem.price.toString()) *
                                                    parseFloat(otcItem.quantity.toString()))
                                                    | currency
                                            }}
                                        </b-td>
                                        <b-td>
                                            <b-icon v-if="!disableFields" icon="x-circle-fill" style="cursor: pointer"
                                                variant="danger" @click="removeItem(otcItem.otcItemId)" />
                                        </b-td>
                                    </b-tr>
                                    <b-tr>
                                        <b-td></b-td>
                                        <b-td></b-td>
                                        <b-td></b-td>
                                        <b-td></b-td>
                                        <b-th>Addon Total:</b-th>
                                        <b-td>{{ AddonTotalSum | currency }}</b-td>
                                        <b-td></b-td>
                                    </b-tr>
                                </b-tbody>
                            </b-table-simple>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <hr />
            <b-row id="delivery-method-row">
                <b-col cols="12" v-if="selectedProgram?.transferToPharmacies?.length ?? 0 > 0" class="mb-3">
                    Transfer To Pharmacy: {{ order.transferToPharmacyId }}
                    <b-select :disabled="disableFields" v-model="order.transferToPharmacyId"
                        :options="selectedProgram?.transferToPharmacies?.map(p => ({ value: p.id, text: p.name }))" />
                </b-col>
                <b-col xl="6">
                    <h5>Delivery Method</h5>
                    <EnumSelector v-model="order.deliveryCode" :disabled="disableFields && routeMode != 'verify'"
                        :enum-codes="deliveryCodes" :display-names="deliveryCodeDisplayNames"
                        label="Only change to override predefined delivery method" />
                    <div v-if="order.patientPickupPreference !== null && order.patientPickupPreference !== undefined"
                         class="mt-2 text-muted small">
                        <b-badge :variant="order.patientPickupPreference ? 'info' : 'secondary'">
                            Patient requested: {{ order.patientPickupPreference ? 'Pickup' : 'Delivery' }}
                        </b-badge>
                    </div>
                </b-col>
                <b-col xl="6">
                    <hr class="d-xl-none">

                    <div class="d-flex align-items-center justify-content-between">
                        <h5>
                            <checkbox :checked="hasPromisedDate" @change="handlePromisedTimeCheck"
                                :disabled="disablePromisedDate">
                                Promised Time
                            </checkbox>
                        </h5>
                        <PromisedTimeSummary v-if="hasPromisedDate" :value="order.promisedDate"
                            :info-only="!order.isPromisedApplicable" />
                    </div>

                    <Transition name="fade">
                        <DateTimeInput ref="promisedTime" v-if="hasPromisedDate" :value="order.promisedDate"
                            @input="handlePromisedDateChange" show-presets :disabled="disablePromisedDate"
                            disallow-past />
                    </Transition>
                </b-col>
            </b-row>
            <hr />
            <!-- PAYMENTS -->
            <b-row id="payments-row">
                <b-col>
                    <h4 class="card-subtitle">Payments</h4>
                    <card-reader v-if="routeMode == 'pickup'" :order-id="order && +order.id"
                        :store-id="Number(selectedStoreId)" @swipe-error="loadOrder" />
                    <b-button v-if="routeMode != 'verify'" v-b-modal="'select-payment'" :disabled="disableEditPayment"
                        variant="success" @click="updateStripeCardList">
                        Edit Payment
                    </b-button>
                    <b-row>
                        <b-col>
                            <label><strong>Order Payments</strong></label>
                            <b-table :items="selectedStripeCards" :fields="paymentTableFields" fixed outlined>
                                <template #cell(actions)="{ item }">
                                    <b-button v-if="canVoidPayment && routeMode != 'verify' && !order.wasShipped"
                                        variant="outline-danger" size="sm"
                                        :disabled="disableEditPayment || isFullyRefunded(item) || voidingPayment"
                                        @click="voidPayment(item)">
                                        <i class="fas fa-trash-alt" aria-hidden="true"></i> Void/Refund
                                    </b-button>
                                </template>
                            </b-table>
                            <strong>Balance Owed: {{ amountOwed | currency }}</strong>
                            <br />
                            <strong v-if="isBalanceCovered" class="fas fa-check-circle"
                                style="color: green" />
                            <strong v-else class="fas fa-times-circle" style="color: red" />
                            <strong>Payment Total: {{ (totalSelected + arAppliedAmount) | currency }}</strong>
                            <span v-if="arAppliedAmount > 0"> (incl. {{ arAppliedAmount | currency }} on account)</span>
                        </b-col>
                    </b-row>

                    <!-- ON ACCOUNT (AR) PAYMENT — the amount charged to the account once the order is
                         verified. Shown as a payment alongside cards; Void/Refund reverses the ledger
                         charge (credits the account back). It never affects a prescription's patient pay. -->
                    <b-row v-if="arPostedAmount > 0" id="ar-posted-payment-row">
                        <b-col>
                            <label><strong>On Account (AR)</strong></label>
                            <div class="d-flex align-items-center">
                                <span>{{ arPostedAmount | currency }} charged to the account</span>
                                <b-button v-if="canVoidArPayment && routeMode != 'verify' && !order.wasShipped"
                                    variant="outline-danger" size="sm" class="ml-3"
                                    :disabled="disableEditPayment || voidingArPayment" @click="voidArPayment">
                                    <i class="fas fa-trash-alt" aria-hidden="true"></i> Void/Refund
                                </b-button>
                            </div>
                        </b-col>
                    </b-row>

                    <b-row v-if="isProgramWithCopayCollectionFlag">
                        <b-col>
                            <checkbox v-model="collectedByPartner" :disabled="disableFields">Collected by Partner
                            </checkbox>
                        </b-col>
                    </b-row>
                    <b-row v-if="routeMode == 'pickup'">
                        <b-col>
                            <b-form-group label="Payment Method">
                                <b-form-radio-group v-model="modeOfPayment" :options="[
                                    { text: 'Card', value: 'card' },
                                    { text: 'Check', value: 'check' },
                                    { text: 'Cash', value: 'cash' },
                                ]" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                    <b-row v-if="modeOfPayment == 'check'">
                        <b-col>
                            <b-form-group label="Check Number">
                                <b-form-input v-model="checkNumber"></b-form-input>
                            </b-form-group>
                        </b-col>
                    </b-row>

                    <!-- ON ACCOUNT (AR) — split-tender: charge part of patient responsibility to an AR account.
                         Hidden once a charge has posted (the read-only On Account line shows it instead), but
                         re-shown after a void (arPostedAmount back to 0) so a corrected election can be made. -->
                    <b-row v-if="arAvailable && (!order.verified || arPostedAmount === 0)" id="ar-payment-row" class="mt-2">
                        <b-col>
                            <b-form-group label="On Account (AR)">
                                <b-row class="align-items-center">
                                    <b-col cols="5">
                                        <b-form-select v-model="arSelectedAccountId" :options="arAccountOptions"
                                            :disabled="disableEditPayment" @change="onArSelectionChange" />
                                    </b-col>
                                    <b-col cols="3">
                                        <b-form-input type="number" step="0.01" min="0" v-model.number="arAmount"
                                            :disabled="disableEditPayment || !arSelectedAccountId" placeholder="Amount"
                                            @change="checkArCredit" />
                                    </b-col>
                                    <b-col cols="4">
                                        <b-button v-if="hasArElection" variant="outline-danger" size="sm"
                                            :disabled="disableEditPayment" @click="clearArElection">
                                            Clear
                                        </b-button>
                                    </b-col>
                                </b-row>
                            </b-form-group>

                            <div v-if="arCreditCheck" class="small">
                                <span>Available Credit: {{ arCreditCheck.availableCredit | currency }}</span>
                                <b-alert :show="arOverLimit" variant="warning" class="mt-1 mb-1">
                                    This charge exceeds the account's credit limit.
                                    <template v-if="canArOverride">
                                        <b-form-input v-model="arOverrideReason" size="sm" class="mt-1 ar-override-reason"
                                            placeholder="Reason for over-limit override (required)" />
                                    </template>
                                    <template v-else>
                                        You do not have permission to authorize an over-limit charge.
                                    </template>
                                </b-alert>
                            </div>
                            <div v-if="arCoveredAmount > 0" class="text-success small">
                                <i class="fas fa-check-circle" />
                                {{ arCoveredAmount | currency }} will be charged to the account when the order is saved.
                            </div>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>

            <b-overlay :show="loadingAcceptanceConfigurations">
                <b-row id="acceptance-configuration-table-row">
                    <b-col>
                        <b-table id="acceptance-configuration-table" :items="acceptanceConfigurations"
                            :fields="acceptanceConfigurationFields" :tbody-tr-class="acceptanceClass"
                            :busy="loadingAcceptanceConfigurations">
                            <template #cell(imageId)="row">
                                <a v-show="row && row.value" v-b-tooltip.hover.top="'View'" href=""
                                    @click.prevent="openDocument(row.item)">
                                    <i class="now-ui-icons ui-1_zoom-bold"></i>
                                    View Document
                                </a>
                            </template>
                            <template #cell(checked)="row">
                                <b-checkbox v-model="row.item.checked" :disabled="row.item.requiresSignature">
                                    <div v-if="row.item.requiresSignature">
                                        This requires an actual signature
                                        <b-button size="sm" class="mx-2" @click="textAcceptanceConfiguration(row.item)">
                                            Text
                                        </b-button>
                                    </div>
                                    <div v-else>
                                        Patient agrees to terms and conditions
                                    </div>
                                </b-checkbox>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
            </b-overlay>

            <b-row v-if="order.id">
                <b-col>
                    <h4>Notes</h4>
                    <NotesForm :entity-id="order.id" :entityType="EntityType.Order" save-button-label="Add Note"
                        text-box-label="Note" @saved="refreshNotes" />
                    <NotesList ref="notesList" :entity-id="order.id" :entity-type="EntityType.Order" :page-size="5" />
                </b-col>
            </b-row>

            <b-row id="shipping-row">
                <b-col>
                    <h4>Shipping</h4>
                    <template v-if="routeMode == 'verify' || routeMode == 'edit'">
                        <b-dropdown v-if="routeMode == 'verify'" :text="prettyPrinterName(selectedPrinter)" variant="outline-primary" dropup>
                            <b-dropdown-item disabled v-if="!hasLocalPrinters">
                                <i class="fa fa-info-circle" />
                                To print to local printers, install<br>the Scriptly Client Service
                            </b-dropdown-item>
                            <b-dropdown-item-button v-for="p in localPrinters" :key="p.name"
                                :active="!p.id && p.name === selectedPrinter?.name" @click="selectPrinter(p)">
                                <i class="fa fa-print"></i>
                                {{ prettyPrinterName(p) }}
                                <div class="d-flex" style="gap: 6px;">
                                    <small v-if="p.default">Local Default</small>
                                    <small v-if="p.status !== 'Unknown'">{{ p.status }}</small>
                                </div>
                            </b-dropdown-item-button>
                            <b-dropdown-divider v-if="printers.length" />
                            <b-dropdown-item v-for="printer in printers" :key="`printer-${printer.id}`"
                                @click="selectPrinter(printer)" :active="printer.id === selectedPrinter?.id">
                                <i class="fa fa-globe"></i>
                                {{ printer.name }}
                            </b-dropdown-item>
                        </b-dropdown>

                        <b-form-group>
                            <b-form-checkbox-group v-model="shippingOptions" @change="markShippingOptionsTouched">
                                <b-row v-if="routeMode == 'verify'">
                                    <b-col>
                                        <b-form-checkbox v-if="!patient.hipaaDocsReceived" value="HIPAADocsInBox">
                                            <b style="color: red">New Patient Paperwork Included</b>
                                        </b-form-checkbox>
                                        <span v-if="patient.hipaaDocsReceived">
                                            HIPAA Documents Received: {{
                                                patient.hipaaDocsReceived | formatLocalDate("MM/DD/YYYY")
                                            }}
                                        </span>
                                    </b-col>
                                </b-row>

                                <b-form-checkbox value="SignatureRequired">Signature Required</b-form-checkbox>
                                <b-form-checkbox value="OverridePriority">Override Priority</b-form-checkbox>
                                <b-form-checkbox :disabled="!shippingOptions.includes('OverridePriority')"
                                    value="SaturdayDelivery">
                                    Saturday Delivery
                                </b-form-checkbox>
                                <b-form-checkbox value="OverrideAddress">Override Address</b-form-checkbox>
                                <b-form-checkbox value="DisableOneRate">Disable OneRate</b-form-checkbox>
                                <b-form-checkbox value="ShipToPrescriber"
                                    :disabled="isShipToPrescriberFromCategory">
                                    Ship to Prescriber
                                </b-form-checkbox>
                            </b-form-checkbox-group>
                        </b-form-group>
                        <!-- Tag-marking warnings (e.g. hazmat/cold-chain) are read-only alerts, not
                             shipping toggles; keep them visible while editing so they aren't lost now
                             that edit mode renders the checkbox group instead of the read-only badges. -->
                        <div v-if="routeMode == 'edit' && tagMarkings.length">
                            <b-badge v-for="marking in tagMarkings" :key="marking" variant="danger" class="ml-1">{{ marking }}</b-badge>
                        </div>
                    </template>
                    <div v-else class="d-flex" style="gap: 6px;">

                        <b-form-group>
                            <b-form-checkbox-group v-model="shippingOptions">
                                <b-form-checkbox value="SignatureRequired">Signature Required</b-form-checkbox>
                            </b-form-checkbox-group>
                        </b-form-group>
                        <b-badge v-if="order?.hipaaDocsInBox" variant="primary">HIPAA Docs in Box</b-badge>
                        <b-badge v-if="order?.signatureRequired" variant="primary">Signature Required</b-badge>
                        <b-badge v-if="order?.overridePriority" variant="primary">Override Priority</b-badge>
                        <b-badge v-if="order?.saturdayDelivery" variant="primary">Saturday Delivery</b-badge>
                        <b-badge v-if="order?.overrideAddress" variant="primary">Override Address</b-badge>
                        <b-badge v-if="order?.disableOneRate" variant="primary">Disable OneRate</b-badge>
                        <b-badge v-for="marking in tagMarkings" :key="marking" variant="danger" class="ml-1">{{ marking }}</b-badge>
                    </div>
                </b-col>

                <b-col v-if="routeMode == 'verify'">
                    <div>
                        <h4 class="mb-1">
                            Weather Forecast
                        </h4>
                        <div v-if="order.minTemp || order.maxTemp" class="weather-info">
                            <div>
                                <i class="fas fa-thermometer-quarter text-primary"></i>
                                <div>
                                    <div class="temp">{{ order.minTemp }}<sub>°F</sub></div>
                                    <small class="text-muted">Low</small>
                                </div>
                            </div>
                            <div>
                                <i class="fas fa-thermometer-three-quarters text-danger"></i>
                                <div>
                                    <div class="temp">{{ order.maxTemp }}<sub>°F</sub></div>
                                    <small class="text-muted">High</small>
                                </div>
                            </div>
                        </div>
                        <div v-else class="text-muted">
                            <i class="fas fa-cloud-sun"></i>
                            Weather data not available
                        </div>
                    </div>
                </b-col>

            </b-row>

            <b-row v-if="order.created" class="text-muted mt-3">
                <b-col>
                    <p class="my-0">Created: {{ formatDate(order.created) }} by {{ order.createdBy }}</p>
                    <p class="my-0">Updated: {{ formatDate(order.updated) }} by {{ order.updatedBy }}</p>
                </b-col>
            </b-row>
        </card>

        <div v-if="isPrinting">
            <b-spinner class="mr-2" label="Spinning"></b-spinner>
            <strong>Printing...</strong>
        </div>

        <b-alert variant="warning" v-model="missingNeededAudit">
            This order's required audit has not yet been completed.
            <b-link :to="{ name: 'OrderAuditDetails', params: { id: order.id } }">
                Audit Page
                <b-icon icon="box-arrow-up-right" />
            </b-link>
        </b-alert>

        <!--Other Open Orders Banner-->
        <b-alert :show="!!orders.length" variant="warning" @click.native="scrollToOpenOrders" class="cursor-pointer">
            <i class="fa fa-warning mr-2" />
            <strong>This patient has {{ orders.length }} additional open {{
                    orders.length === 1 ? "order" : "orders"
                }}.</strong>
        </b-alert>

        <PositiveIdGate
            ref="positiveIdGate"
            :enabled="positiveIdEnabled && isDispenseSave && positiveIdConfigLoaded && positiveIdTargetId !== null"
            :enabled-methods="positiveIdMethods"
            :action-type="PositiveIdActionType.Dispense"
            :target-entity-type="PositiveIdTargetEntityType.Patient"
            :target-entity-id="positiveIdTargetId || ''"
            @verified="onPositiveIdVerifiedForSave"
            @cancelled="onPositiveIdCancelled">
            <template #default="{ requestVerification, verifying }">
                <NewClearSaveButtons v-show="!isPrinting" :disable-cancel="false" :disable-clear="disableClear"
                    :disable-save="disableSave || verifying || positiveIdNotReady" disable-save-after-click :titles="buttonTitles"
                    :tooltips="buttonTooltips" :save-release-id="saveReleaseId"
                    @cancel-clicked="backToPreviousPage" @clear-clicked="clearOrCancel" @save-clicked="requestVerification" />
            </template>
        </PositiveIdGate>
        <b-row v-if="canSetShipped || canManuallyShip" class="justify-content-center text-center">
            <b-col cols="auto" v-if="canSetShipped">
                <b-button variant="warning" @click="updateOrderError">Set Shipped</b-button>
            </b-col>
            <b-col cols="auto" v-if="canManuallyShip">
                <b-button variant="primary" v-b-modal.manual-ship>Manual Ship...</b-button>
            </b-col>
        </b-row>

        <b-modal id="manual-ship" size="lg" title="Manual Ship" ok-title="Ship Order" @ok="completeManualShip">
            <b-row align-v="baseline" cols="1" cols-lg="2">
                <b-col>
                    <b-form-group label="Shipment Type">
                        <enum-selector v-model="shipment.shipmentType" :enum-codes="deliveryCodes"
                            :display-names="deliveryCodeDisplayNames" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Carrier" description="Selecting a carrier enables Track Package in the patient app.">
                        <b-form-select v-model="shipment.serviceType" :options="manualShipCarrierOptions" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Tracking Number">
                        <b-input v-model="shipment.trackingNumber" />
                    </b-form-group>
                </b-col>
                <b-col>
                    <b-form-group label="Shipped Date">
                        <!-- value-format sends a bare calendar date (no time/timezone) so the server
                             can anchor it to the store's timezone; avoids a browser-timezone day shift. -->
                        <el-date-picker v-model="shipment.shipped" format="MM/dd/yyyy" value-format="yyyy-MM-dd" type="date" />
                    </b-form-group>
                </b-col>
            </b-row>
            <hr>
            <b-row>
                <b-col cols="12">
                    <b-form-group label="Address Line 1">
                        <b-input v-model="shipment.address1" />
                    </b-form-group>
                </b-col>
                <b-col cols="12">
                    <b-form-group label="Address Line 2">
                        <b-input v-model="shipment.address2" />
                    </b-form-group>
                </b-col>
                <b-col lg="4">
                    <b-form-group label="City">
                        <b-input v-model="shipment.city" />
                    </b-form-group>
                </b-col>
                <b-col lg="4">
                    <b-form-group label="State">
                        <b-input v-model="shipment.state" />
                    </b-form-group>
                </b-col>
                <b-col lg="4">
                    <b-form-group label="Postal Code">
                        <b-input v-model="shipment.zip" />
                    </b-form-group>
                </b-col>
            </b-row>

            <FedExDisclaimer />
        </b-modal>

        <b-modal id="delivery-log-modal" size="xl" title="Delivery Log" hide-footer lazy>
            <DeliveryLogComponent v-if="order.id" :order-id="+order.id" />
        </b-modal>

        <b-modal id="merge-modal" size="xl" title="Merge Preview" @ok="completeMerge">
            <b-row>
                <b-col v-for="ord in [order, mergeOrder]" :key="ord.id">
                    {{ ord.id }} - {{ ord.storeName }} - {{ ord.created | formatLocalDate("L") }} -
                    {{ orderStatus[ord.status] }}
                    <hr>
                    <ul>
                        <li v-for="rx in ord.prescriptions" :key="rx.id">
                            {{ rx.dispensed }} - {{ rx.drugName }}
                        </li>
                    </ul>
                </b-col>
            </b-row>
        </b-modal>
        <b-modal id="select-payment" size="xl" title="Select Payment" @ok="savePayments">
            <b-row>
                <b-col>
                    <StripeVueCard :patient="patient" @cardAdded="newCardAdded"></StripeVueCard>
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-table :items="stripeCardListGetter" :fields="stripeCardSelectionFields"
                        class="table-shopping border">
                        <template #cell(brand)="{ item }">
                            <span class="d-inline-flex align-items-center" style="gap: 6px; text-transform: capitalize;">
                                <i :class="brandIcon(item.brand)" class="fa-lg fa-fw" aria-hidden="true"></i>
                                {{ item.brand }}
                            </span>
                        </template>
                        <template #cell(expire)="{ item }">
                            {{ item.expMonth }}/{{ item.expYear }}
                        </template>
                        <template #cell(amount)="{ item }">
                            <fg-input v-model.number="item.amount" :no-wheel="true" placeholder="$0.00"
                                type="number">
                            </fg-input>
                        </template>
                    </b-table>
                </b-col>
            </b-row>
            <b-row>
                <b-col class="text-right">
                    <strong>Balance Owed: {{ amountOwed | currency }}</strong>
                    <br />
                    <strong v-if="totalSelectedPreview - amountOwed == 0" class="fas fa-check-circle"
                        style="color: green" />
                    <strong v-if="totalSelectedPreview - amountOwed != 0" class="fas fa-times-circle"
                        style="color: red" />
                    <strong>Payment Total: {{ totalSelectedPreview | currency }}</strong>
                </b-col>
            </b-row>
        </b-modal>

        <b-modal id="cancel-order-modal" title="Cancel Order" no-fade centered ok-title="Cancel Order"
            ok-variant="danger" @ok="cancelOrder">
            <p>
                <strong>Are you sure you want to cancel this order?</strong> This action cannot be reverted.
            </p>

            <b-form-group v-if="cancelReasonOptions?.length" label="Cancellation Reason">
                <b-form-select v-model="cancelReasonSelection" :options="cancelReasonOptions" />
            </b-form-group>
        </b-modal>

        <AddressApprovalModal
            v-model="showAddressApproval"
            :original-address="addressValidation?.originalAddress"
            :normalized-address="addressValidation?.normalizedAddress"
            @approve="onAddressApproved"
            @override="onAddressOverridden"
        />

        <DuplicateOrderModal :visible.sync="showDuplicateModal" :open-orders="duplicateOpenOrders"
            :can-merge="canMergeOrders" @create-anyway="duplicateCreateAnyway" @merge="duplicateMerge" />

        <FedExDisclaimer />
    </div>
</template>

<script lang="ts">
import { Component, Mixins, Ref, Watch } from "vue-property-decorator";
import { Lock, LockHandler } from "@/mixins/LockHandler";
import { Prescription, RefillStatusCode } from "@/models/Prescription";
import { DeliveryCode, DeliveryCodeDisplayNames, DeliveryCodeImportanceRank } from "@/models/DeliveryCode";
import FedExDisclaimer from "@/components/Shipping/FedExDisclaimer.vue";
import { Printer } from "@/models/Printer";
import { Store } from "@/models/Store";
import { Order, OrderOTCItem, OrderStatus } from "@/models/Order";
import OrdersListComponent from "@/components/Order/OrdersListComponent.vue";
import DuplicateOrderModal from "@/components/Order/DuplicateOrderModal.vue";
import OrderService from "@/services/OrderService";
import { Payment, StripeCard } from "@/models/Payment";
import { cardBrandIcon } from "@/util/stripeCardBrand";
import { orderContentSignature, refusedBeforeSave, resolveCreateTokenWithProbe, shouldResetTokenOnError } from "@/util/orderIdempotency";
import { deliveryLogScriptKeys, isDeliveryLogStale } from "@/util/deliveryLogFreshness";
import { Patient, PatientAddress } from "@/models/Patient";
import { OTCItem } from "@/models/OTCItem";
import { DatePicker } from "element-ui";
import EnumSelector from "@/components/EnumSelector.vue";
import axios from "axios";
import Axios, { AxiosError, AxiosRequestConfig } from "axios";
import moment from "moment";
import SearchComponent from "@/components/SearchComponent.vue";
import NewClearSaveButtons from "@/components/NewClearSaveButtons.vue";
import { ButtonTitles } from "@/components/ButtonTitles";
import PrescriptionsListComponent from "@/components/Prescription/PrescriptionsListComponent.vue";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import StripeVueCard from "@/components/Payments/StripeVueCardcomponent.vue";
import Checkbox from "@/components/Inputs/Checkbox.vue";
import { Program } from "@/models/Program";
import { AcceptanceConfiguration, AcceptanceConfigurationStatus } from "@/models/AcceptanceConfiguration";
import { AcceptanceRecord } from "@/models/AcceptanceRecord";
import { BCard, BvTableFieldArray } from "bootstrap-vue";
import CardReader from "@/components/Cards/CardReader.vue";
import OrderAuditDetails from "@/pages/Order/OrderAuditDetails.vue";
import DateTimeInput from "@/components/Inputs/dateTimeInput.vue";
import PromisedTimeSummary from "@/components/Order/PromisedTimeSummaryComponent.vue";
import HistoryModal from "@/components/Modals/HistoryModal.vue";
import { EntityType } from "@/models/EntityType";
import NotesList from "@/components/Note/NotesList.vue";
import NotesForm from "@/components/Note/NotesForm.vue";
import TasksPage from "@/pages/Tasks/TasksPage.vue";
import { UserTask } from "@/models/UserTask";
import { Shipment } from "@/models/Shipment";
import { AddressValidationResult } from "@/models/AddressValidation";
import AddressApprovalModal from "@/components/Shipping/AddressApprovalModal.vue";
import DeliveryLogComponent from "@/components/EmbeddedReports/DeliveryLogComponent.vue";
import PositiveIdGate from "@/components/Common/PositiveIdGate.vue";
import PositiveIdService, {
    PositiveIdActionType,
    PositiveIdTargetEntityType,
    POSITIVE_ID_TOKEN_HEADER,
} from "@/services/PositiveIdService";
import { ARAccountSummary, ARCreditCheck } from "@/models/AR";

Component.registerHooks(["beforeRouteEnter"]);

@Component({
    computed: {
        OrderAuditDetails() {
            return OrderAuditDetails;
        },
    },
    components: {
        AddressApprovalModal,
        DeliveryLogComponent,
        FedExDisclaimer,
        HistoryModal,
        CardReader,
        SearchComponent,
        NewClearSaveButtons,
        PositiveIdGate,
        [DatePicker.name]: DatePicker,
        PrescriptionsListComponent,
        StripeVueCard,
        OrdersListComponent,
        DuplicateOrderModal,
        EnumSelector,
        Checkbox,
        DateTimeInput,
        PromisedTimeSummary,
        NotesList,
        NotesForm,
        TasksPage,
        ShipToPrescriberBanner: () => import("@/components/Order/ShipToPrescriberBanner.vue"),
        CompletionItemsBanner: () => import("@/components/Order/CompletionItemsBanner.vue"),
    },
})
export default class OrderDetailsPage extends Mixins(LockHandler) {
    beforeRouteEnter(_to: any, from: any, next: any) {
        next((vm: any) => (vm.from = from));
    }

    PositiveIdActionType = PositiveIdActionType;
    PositiveIdTargetEntityType = PositiveIdTargetEntityType;

    private order: Order = new Order();
    private showDuplicateModal = false;
    private duplicateOpenOrders: Order[] = [];
    private allowDuplicateOrder = false;
    private pendingMergeTargetId: number | null = null;
    private orderLock: Lock | null = null;
    private positiveIdEnabled = false;
    private positiveIdMethods: string[] | null = null;
    private positiveIdConfigLoaded = false;
    private verifiedToken = '';
    private patient: Patient = new Patient();
    private selectedStore: Store = new Store();
    private orderStatus = OrderStatus;
    private availableToOrder: Prescription[] = [];
    protected orders: Order[] = [];
    private showCancelled = false;
    private showShipped = false;
    protected deliveryCodes = DeliveryCode;
    protected deliveryCodeDisplayNames = DeliveryCodeDisplayNames;
    private from: any = null;

    private dataPrescriptions: Array<Prescription> = [];
    private dataOrderItems: Array<OrderOTCItem> = [];

    // The prescription list as last persisted. The delivery log is rendered server-side from
    // committed state, so this is what lets us tell whether the PDF would still match the screen.
    private savedPrescriptionKeys: Array<string> = [];

    private selectedPayments: Array<Payment> = [];

    private stripeCardList: Array<StripeCard> = [];
    private selectedStripeCards: Array<StripeCard> = [];
    private voidingPayment = false;
    private voidingArPayment = false;

    // On-account (AR) split-tender election. The elected portion of patient responsibility is
    // charged to the chosen account at verification; cash/CC/check cover the rest as usual.
    private arEnabled = false;
    private arAccounts: ARAccountSummary[] = [];
    private arSelectedAccountId: number | null = null;
    private arAmount: number | null = null;
    private arCreditCheck: ARCreditCheck | null = null;
    private arOverrideReason = "";

    private shippingOptions: Array<string> = ["HIPAADocsInBox"];
    private shippingOptionsTouched = false;
    private tagMarkings: Array<string> = [];
    private isShipToPrescriberFromCategory = false;
    private printers: Array<Printer> = [];
    private selectedPrinter: any = {};

    private collectedByPartner = false;
    protected isSubmitting = false;
    // Bumped to release the Save button's click latch; see releaseSaveButton.
    protected saveReleaseId = 0;
    private clientRequestId: string | null = null;
    private lastSubmitSignature: string | null = null;
    private isUpdatingError = false;

    protected cancelReasonSelection: string | null = null;
    protected cancelReasonOptions: any[] | null = null;

    protected manualShipCarrierOptions = [
        { value: null, text: "— Select carrier —" },
        { value: "fedex", text: "FedEx" },
        { value: "ups", text: "UPS" },
        { value: "usps", text: "USPS" },
        { value: "other", text: "Other" },
    ];

    protected localPrinters: any[] = [];
    protected shipment: Shipment = new Shipment();
    protected selectedPharmacy: any;

    protected availableRxFields = [
        {
            key: "productNameShort",
            label: "Drug",
        },
        {
            key: "rxId",
            label: "Rx",
        },
        {
            key: "fillDate",
            formatter: (d: Date) => moment(d).format("L"),
        },
        {
            key: "patientPay",
            label: "Copay",
            formatter: (copay: any) => `$${copay == null ? "0.00" : copay.toFixed(2)}`,
        },
        {
            key: "programName",
            label: "Program",
        },
        {
            key: "storeName",
            label: "Store",
        },
        {
            key: "verifyButton",
            label: "",
        },
    ];

    private buttonTitles: any = {
        new: "",
        clear: "Clear",
        save: "Save Changes",
        cancel: "Back to Dashboard",
    };

    private buttonTooltips: ButtonTitles = {};

    private selectedStoreId: string = "";
    private unfilteredAvailableToOrder: Prescription[] = [];
    private preselectRxId: string | null = null;
    private blockC2Cache: boolean | null = null;
    private selectedProgram: Program = new Program();
    private haveSelectedPrescriptions = false;
    private isProgramWithCopayCollectionFlag: boolean = false;
    private isProgrammaticSelect = false;
    private disableDetermineDeliveryCode = true;
    protected isPrinting: boolean = false;
    private addressValidation: AddressValidationResult | null = null;
    private showAddressApproval: boolean = false;
    private modeOfPayment = "card";
    private checkNumber: string = "";
    protected acceptanceConfigurations: AcceptanceConfiguration[] = [];
    private loadingAcceptanceConfigurations = false;
    @Ref("openOrdersCard") private openOrdersCard?: BCard;

    EntityType = EntityType;

    private currencyFormatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
    });

    protected paymentTableFields: BvTableFieldArray = [
        {
            key: "brand",
            label: "Card",
        },
        {
            key: "cardNumber",
            label: "Number",
        },
        {
            key: "exp",
            label: "Expiration Date",
            formatter: (_value, _key, item) => `${item.expMonth}/${item.expYear}`,
        },
        {
            key: "amount",
            formatter: this.currencyFormatter.format,
        },
        {
            key: "amountCaptured",
            label: "Captured",
            formatter: this.currencyFormatter.format,
        },
        {
            key: "amountRefunded",
            label: "Refunded",
            formatter: this.currencyFormatter.format,
            class: "text-danger",
        },
        {
            key: "actions",
            label: "",
        },
    ];

    protected stripeCardSelectionFields: BvTableFieldArray = [
        { key: "brand", label: "Card" },
        { key: "cardNumber", label: "Number" },
        { key: "expire", label: "Expire" },
        { key: "amount", label: "Amount" },
    ];

    brandIcon(brand: string): string {
        return cardBrandIcon(brand);
    }

    formatDate(date: any) {
        return moment.utc(date).local().format("LLL");
    }

    isAddressAllowed(address: PatientAddress): boolean {
        if (this.order.deliveryCode == DeliveryCode.WillCall) return true;
        return this.selectedStore?.allowedStates?.some(s => address.state?.toUpperCase().trim() === s.toUpperCase().trim()) ?? true;
    }

    get isSelectedAddressAllowed(): boolean {
        if (this.order.deliveryCode == DeliveryCode.WillCall) return true;

        if (!this.selectedStore?.allowedStates?.length) return true;

        const addressSelected = this.order.patientAddressId
            ? this.availableAddresses.find(a => a.id == this.order.patientAddressId)
            : this.availableAddresses.find(a => a.isPrimary);

        if (!addressSelected) return false;

        return this.isAddressAllowed(addressSelected);
    }

    get availableAddresses(): PatientAddress[] {
        const patientAddresses = this.patient?.addresses || [];

        // If order has a specific address and it's not in patient addresses, add it
        if (this.order?.patientAddress && !patientAddresses.some(pa => pa.id === this.order.patientAddress?.id)) {
            return [...patientAddresses, this.order.patientAddress];
        }

        return patientAddresses;
    }

    get canManuallyShip() {
        const hasPermission = this.$permissions.some(p => p.permissionCodeName === "Admin/Manual Ship");
        const goodStatus = this.order.status === OrderStatus.Created || this.order.status === OrderStatus.Processing;
        // PV2 must be complete before a manual ship — the server enforces this too; this hides the
        // button so techs aren't offered an action that will be rejected. pv2Complete also catches
        // cancelled PV2s, which isAllVerified (missing-PV2 only) does not.
        return hasPermission && goodStatus && this.isAllVerified && this.pv2Complete;
    }

    // Every prescription on the order must carry a PV2 that is present and not cancelled — the same
    // rule the server enforces in RejectIfPv2Unverified. Gates the manual-ship and will-call pickup
    // actions so techs aren't offered a release the server will reject (which, for will-call, would
    // leave the order half-mutated). pv2Cancelled is undefined on scripts not loaded via the order,
    // which reads as "not cancelled" — the safe default matching prior behavior.
    get pv2Complete(): boolean {
        return this.dataPrescriptions.every((rx) => (rx.pV2ID ?? 0) > 0 && !rx.pv2Cancelled);
    }

    get disableStoreSelection() {
        return !!(this.disableFields || (this.selectedStore?.id && this.dataPrescriptions?.length));
    }

    get disableProgramSelection() {
        return !!(
            this.disableFields ||
            (this.selectedProgram?.id && this.dataPrescriptions?.length)
        );
    }

    get missingNeededAudit() {
        return this.order.requiresAudit && !this.order.auditId;
    }

    get isAllVerified() {
        if (this.missingNeededAudit) return false;

        let allVerified = true;
        this.availableToOrder.forEach((rx) => {
            if (!rx.pV2ID) allVerified = false;
        });
        return allVerified;
    }

    get hasErrorPermission(): boolean {
        return this.$permissions.some((p) => p.permissionCodeName == "Admin/Order Error");
    }

    get canErrorStatus(): boolean {
        return this.hasErrorPermission && (this.order.status == OrderStatus.Error || this.order.wasShipped || this.order.status == OrderStatus.Canceled);
    }

    get canSetShipped() {
        return this.hasErrorPermission && this.order.status === OrderStatus.Created && this.order.shipmentId > 0;
    }

    get disableClear(): boolean {
        const canCancel = this.$permissions.some((p) => p.name == "Cancel Order");
        const shippedCheck = this.order.wasShipped && !this.hasErrorPermission;
        return !canCancel || !(this.order && this.order.id) || this.hipaaRequired || shippedCheck;
    }

    get disableSave() {
        const addressValid = this.isSelectedAddressAllowed;
        const editNotValid = ((!this.isFormValid || this.isOrderVerified) && this.routeMode == 'edit');
        const verifyNotValid = (!this.isAllVerified && this.routeMode == 'verify');
        // Will-call pickup is a two-phase save (persist ReadyForPickup, then mark PickedUp); the
        // server now rejects the second phase on an unverified PV2, so gate the button here too or the
        // order is left mutated with only a generic error. Mirrors the manual-ship PV2 gate.
        const pickupNotValid = ((!this.isFormValid || !this.pv2Complete) && this.routeMode == 'pickup');
        const patientDeceased = !!this.patient.deceased;
        const acceptanceNotValid = this.loadingAcceptanceConfigurations || this.acceptanceConfigurations.some(ac => (!ac.checked && ac.mandatory));
        // isSubmitting is claimed synchronously on entry to the save handler, so it guards re-entry on
        // its own and keeps the button down for the whole request; the click latch backs it up.
        return this.isSubmitting || this.hipaaRequired || editNotValid || verifyNotValid || pickupNotValid || acceptanceNotValid || !addressValid || patientDeceased;
    }

    // The balance is covered when collected tenders (card/cash/check) plus the applied AR amount
    // equal the amount owed. Rounded to cents to avoid float drift from the AR portion.
    get isBalanceCovered(): boolean {
        return Number((this.totalSelected + this.arAppliedAmount).toFixed(2)) === this.amountOwed;
    }

    get isFormValid() {
        let isStoreSelected = this.selectedStore?.id;
        const isPaid = this.isBalanceCovered;
        const collectedByPartner = this.isProgramWithCopayCollectionFlag && this.collectedByPartner;
        const pickupPayment = (this.order.deliveryCode == DeliveryCode.WillCall && (this.routeMode != "pickup" || this.modeOfPayment != "card"));
        const isDeliveryMethodSelected = !!(
            this.order.deliveryCode || this.order.deliveryCode == DeliveryCode.Standard
        );
        const isPrescriptionsSelected = !!this.dataPrescriptions?.length;
        return (
            isStoreSelected &&
            isPrescriptionsSelected &&
            isDeliveryMethodSelected &&
            !this.disableFields &&
            (isPaid || collectedByPartner || pickupPayment)
        );
    }

    get hipaaRequired() {
        return (
            this.routeMode == "verify" &&
            this.patient.hipaaDocsReceived == null &&
            !this.shippingOptions.includes("HIPAADocsInBox")
        );
    }

    get isOrderCancelled(): boolean {
        return this.order?.status == this.orderStatus.Canceled;
    }

    // Printable at every status but Canceled, so the log can be packed before the order ships and
    // reprinted afterward if the paper copy is lost.
    get canPrintDeliveryLog(): boolean {
        return !!this.order?.id && !this.isOrderCancelled;
    }

    // The report is rendered server-side from committed state and never sees the in-memory list, so
    // an unsaved add or removal would print a document that silently disagrees with the screen.
    get deliveryLogIsStale(): boolean {
        return isDeliveryLogStale(this.dataPrescriptions, this.savedPrescriptionKeys);
    }

    get deliveryLogTooltip(): string {
        return this.deliveryLogIsStale
            ? "Save the order to print an up-to-date delivery log — the prescriptions on this order have changed since it was last saved."
            : "Printable proof of delivery for this order, with a signature and date line for the recipient.";
    }

    openDeliveryLog(): void {
        if (this.deliveryLogIsStale) return;
        this.$bvModal.show("delivery-log-modal");
    }

    get isShipToPrescriber(): boolean {
        return this.shippingOptions.includes("ShipToPrescriber");
    }

    get isOrderVerified(): boolean {
        const isVerified = !!this.order.verified && this.order.deliveryCode != DeliveryCode.WillCall;
        return isVerified;
    }

    get disableFields(): boolean {
        const isVerifying = this.routeMode == "verify";
        return (
            !(this.orderLock || (this.order && !this.order.id)) ||
            isVerifying ||
            this.isOrderCancelled ||
            this.order.wasShipped
        );
    }

    get disablePromisedDate(): boolean {
        return this.disableFields;
    }

    get hasPromisedDate(): boolean {
        return !!this.order.promisedDate;
    }

    get disableEditPayment(): boolean {
        return this.disableFields || (this.routeMode == "pickup" && this.modeOfPayment != "card");
    }

    get StoreAndRxSelected() {
        return this.haveSelectedPrescriptions && this.selectedStoreId != "";
    }

    get rxTable(): any & { selectRow: (index: any) => void } {
        return this.$refs.rxTable as any & { selectRow: (index: any) => void };
    }

    get AddonTotalSum(): string {
        let total: number;
        total = this.dataOrderItems
                    .map((item) => {
                        const price = item.price;
                        const qty = item.quantity;
                        const tax = item.tax ? +item.tax : 0;
                        return price * qty + tax;
                    })
                    .reduce((itemTotal, grandTotal) => {
                        return itemTotal + grandTotal;
                    }, 0);
        return total.toFixed(2);
    }

    get routeOrderId(): number {
        const res = parseInt(this.$route.params.orderId);
        return isNaN(res) ? 0 : res;
    }

    get isDispenseSave(): boolean {
        return this.routeMode == "pickup" || this.routeMode == "edit";
    }

    /** Resolved positive-ID target. Null when patient isn't loaded yet — Save must stay disabled. */
    get positiveIdTargetId(): string | null {
        const n = +(this.patient?.id as any);
        return Number.isFinite(n) && n > 0 ? String(n) : null;
    }

    /** True when positive-ID is on AND target/config aren't ready — Save must be blocked. */
    get positiveIdNotReady(): boolean {
        return this.positiveIdEnabled
            && this.isDispenseSave
            && (!this.positiveIdConfigLoaded || this.positiveIdTargetId === null);
    }

    async fetchPositiveIdConfig() {
        const cfg = await PositiveIdService.getConfig();
        this.positiveIdEnabled = cfg.enabled;
        this.positiveIdMethods = cfg.methods;
        this.positiveIdConfigLoaded = true;
    }

    onPositiveIdVerifiedForSave(payload: { verifiedToken: string }) {
        this.verifiedToken = payload.verifiedToken;
        this.save();
    }

    onPositiveIdCancelled() {
        this.clearDuplicateIntent();
        this.releaseSaveButton();
    }

    /**
     * The Save button latches on click so a second click cannot land during the awaits that run
     * before isSubmitting is set. Every way a save attempt can end — success, error, an abandoned
     * modal — clears one of these flags, so releasing here covers them all without threading a
     * reset through each branch. isSubmitting stays in disableSave to cover the in-flight window
     * once the latch is released.
     */
    @Watch("isSubmitting")
    @Watch("isPrinting")
    @Watch("showDuplicateModal")
    @Watch("showAddressApproval")
    onSaveAttemptSettled(pending: boolean) {
        if (!pending) this.releaseSaveButton();
    }

    // Bound to the buttons rather than reached through a ref: a ref lookup returns undefined the moment
    // the buttons move behind another component, and a latch that is never released leaves Save dead
    // until the page is reloaded. A bound prop travels with the markup instead.
    private releaseSaveButton() {
        this.saveReleaseId++;
    }

    private clearDuplicateIntent() {
        this.allowDuplicateOrder = false;
        this.pendingMergeTargetId = null;
    }

    get routeMode(): string {
        if (this.$router.currentRoute.name == "OrderVerification") return "verify";
        if (this.$router.currentRoute.name == "OrderPickup") return "pickup";
        return "edit";
    }

    get patientId(): number {
        let res = parseInt(this.$route.params.patientId);
        return isNaN(res) ? this.order.patientID || 0 : res;
    }

    get patientName() {
        let res = "";

        if (this.patient.id) res = this.patient.displayNamesForPerson();

        return res;
    }

    get pendingToBeVerified() {
        return this.dataPrescriptions.some((rxRow) => (rxRow.pV2ID || 0) == 0);
    }

    get totalSelected() {
        let total = 0;
        for (let i = 0; i < this.selectedStripeCards.length; i++) {
            const selectedCard = this.selectedStripeCards[i];
            total += selectedCard.amount - selectedCard.amountRefunded;
        }
        return total;
    }

    get totalSelectedPreview() {
        let total = 0;
        for (let i = 0; i < this.stripeCardList.length; i++) {
            const card = this.stripeCardList[i];
            total += card.amount - card.amountRefunded;
        }
        return total;
    }

    get amountOwed() {
        let balance = 0.0;
        for (let i = 0; i < this.dataPrescriptions.length; i++) {
            balance += this.dataPrescriptions[i].patientPay ?? 0;
        }

        let addonTotal = this.dataOrderItems
                             .map((item) => {
                                 const price = item.price;
                                 const qty = item.quantity;
                                 const tax = item.tax ? +item.tax : 0;
                                 return price * qty + tax;
                             })
                             .reduce((itemTotal, grandTotal) => {
                                 return itemTotal + grandTotal;
                             }, 0);
        balance = balance + addonTotal;

        return Number(balance.toFixed(2));
    }

    // ----- On-account (AR) split-tender -----

    // AR may be elected whenever the feature is enabled and the patient has at least one active AR
    // account to charge — including brand-new orders, since the election is saved with the order.
    get arAvailable(): boolean {
        return this.arEnabled && this.arAccountOptions.length > 1;
    }

    // Active accounts only (status 1 = Active); a leading "None" clears the election.
    get arAccountOptions(): Array<{ value: number | null; text: string }> {
        const active = this.arAccounts
                           .filter(a => a.status === 1)
                           .map(a => ({
                               value: a.id,
                               text: `${a.accountNumber ?? "Account " + a.id} — Balance ${this.currency(a.currentBalance)}`,
                           }));
        return [{ value: null, text: "— None —" }, ...active];
    }

    get arOverLimit(): boolean {
        return !!this.arCreditCheck?.overLimit;
    }

    get canArOverride(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Credit Limit/Override At Checkout");
    }

    // The AR-elected amount that counts toward the balance owed. An election is usable when an
    // account and positive amount are chosen, the amount does not exceed the balance, and any
    // over-limit charge is backed by a permitted user supplying a reason (or was already approved on
    // the order). The amount is recorded when the order is saved, not before.
    get arCoveredAmount(): number {
        if (!this.arSelectedAccountId || !this.arAmount || this.arAmount <= 0) return 0;
        if (this.arAmount > this.amountOwed) return 0;
        if (this.arOverLimit
            && !((this.canArOverride && this.arOverrideReason.trim().length > 0) || this.order.arOverrideApproved)) {
            return 0;
        }
        return this.arAmount;
    }

    // The AR amount actually posted to the account ledger for this order (0 before verification or
    // once the charge has been voided/reversed). This is the AR payment that can be voided/refunded.
    get arPostedAmount(): number {
        return this.order?.arPosted ?? 0;
    }

    // The AR tender applied to this order. Once verified, the ledger is authoritative — use the net
    // posted amount, which drops to 0 after a void/reversal. Before verification nothing has posted,
    // so the pending checkout election is what will be tendered. Either way AR only offsets the
    // balance owed as a payment; it never changes a prescription's patient pay.
    get arAppliedAmount(): number {
        return this.order?.verified ? this.arPostedAmount : this.arCoveredAmount;
    }

    // Voiding a posted AR payment reverses the ledger charge; gated on AR account edit rights.
    get canVoidArPayment(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Accounts Receivable/Account/Edit");
    }

    private async initAr(): Promise<void> {
        try {
            const response = await axios.get<boolean>("/configuration/enableAR");
            this.arEnabled = response.data === true;
        } catch {
            this.arEnabled = false;
        }
        if (this.arEnabled) await this.loadArAccounts();
    }

    private async loadArAccounts(): Promise<void> {
        if (!this.patientId) {
            this.arAccounts = [];
            return;
        }
        try {
            const response = await axios.get<ARAccountSummary[]>(`/AR/patients/${this.patientId}/accounts`);
            this.arAccounts = response.data ?? [];
        } catch {
            this.arAccounts = [];
        }
    }

    onArSelectionChange(): void {
        this.arCreditCheck = null;
        this.arOverrideReason = "";
        if (this.arSelectedAccountId && this.arAmount && this.arAmount > 0) this.checkArCredit();
    }

    async checkArCredit(): Promise<void> {
        if (!this.arSelectedAccountId || !this.arAmount || this.arAmount <= 0) {
            this.arCreditCheck = null;
            return;
        }
        try {
            const response = await axios.get<ARCreditCheck>(
                `/AR/accounts/${this.arSelectedAccountId}/credit-check`, { params: { amount: this.arAmount } });
            this.arCreditCheck = response.data;
        } catch (error) {
            this.arCreditCheck = null;
            this.$notification(NotificationOptions.error(error));
        }
    }

    // Whether an account/amount has been entered, so a Clear affordance can be shown.
    get hasArElection(): boolean {
        return !!this.arSelectedAccountId || !!this.arAmount;
    }

    // Local-only reset; the cleared state is persisted when the order is saved.
    clearArElection(): void {
        this.arSelectedAccountId = null;
        this.arAmount = null;
        this.arCreditCheck = null;
        this.arOverrideReason = "";
    }

    private currency(value?: number | null): string {
        return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value ?? 0);
    }

    get acceptanceConfigurationFields(): BvTableFieldArray {
        return [
            {
                key: "id",
                label: "Item",
                formatter: (_value: number, _key: string, item: AcceptanceConfiguration) => item.toString(),
            },
            {
                key: "status",
                label: "Status",
                formatter: (value: AcceptanceConfigurationStatus) => AcceptanceConfigurationStatus[value],
            },
            {
                key: "imageId",
                label: "Document",
            },
            {
                key: "checked",
                label: "Accepted",
            },
        ];
    }

    created() {
        this.fetchPositiveIdConfig();
        this.initAr();
        const rxIdQuery = this.$route.query.rxId;
        this.preselectRxId = (Array.isArray(rxIdQuery) ? rxIdQuery[0] : rxIdQuery) || null;
        if (this.routeMode == "verify") {
            this.buttonTitles.cancel = "Back to Verification Queue";
            this.buttonTitles.save = "Print Label";
            this.buttonTooltips = {
                ...this.buttonTooltips,
                save: "Validates the shipping address and prints the carrier shipping label for the verified order — tracking barcode and ship-to address. This is the label that adheres to the outside of the package.",
            };
            this.loadPrinters();
            this.getLocalPrinters();
        } else if (this.routeMode == "pickup") {
            this.buttonTitles.save = "Pick Up";
        } else {
            this.fetchAvailableRx();
        }

        if (this.routeOrderId) {
            this.acquireOrderLock()
                .then(() => this.loadOrder());
        } else {
            this.initObject();
            this.loadPatient();
        }
    }

    async acquireOrderLock() {
        try {
            // This page instance is reused on param-only navigation (order A → B), so
            // setExclusiveLock must reap the previous order's lock.
            const result = await this.setExclusiveLock(`/Order/${this.routeOrderId}/lock`, 60000);
            // A null return means a newer acquisition superseded this one; skip the assignment
            // so a late-resolving superseded call can't clobber the winning call's orderLock.
            if (result) this.orderLock = result;
        } catch (err) {
            // No lock is held on failure; a stale truthy orderLock from the previous
            // order would leave disableFields false and the form editable.
            this.orderLock = null;
            const error = err as AxiosError;
            if (error.response && error.response.status == 418) {
                // Order locked
                this.showLockedMessage(
                    EntityType.Order,
                    this.routeOrderId?.toString(),
                    this.routeOrderId?.toString(),
                    error.response.data,
                );
            }
        }
    }

    loadOrder() {
        return axios
            .get(`/Order/${this.routeOrderId}`)
            .then((response) => {
                this.order = new Order(response.data);
                axios
                    .get(`/Order/${this.routeOrderId}/tag-markings`)
                    .then((r) => { this.tagMarkings = r.data ?? []; })
                    .catch(() => { this.tagMarkings = []; });
                this.dataOrderItems = this.order.items;
                this.selectedStore = new Store();
                this.selectedStore.id = this.order.storeID;
                this.selectedStore.name = this.order.storeName;

                // Reflect any persisted AR election so it counts toward the balance and shows in the
                // UI. Re-run the credit check so over-limit state is known for the loaded amount.
                this.arSelectedAccountId = this.order.arAccountId ?? null;
                this.arAmount = this.order.arChargeAmount ?? null;
                this.arOverrideReason = this.order.arOverrideReason ?? "";
                if (this.arSelectedAccountId && this.arAmount) this.checkArCredit();
                this.shippingOptions = ["HIPAADocsInBox"];
                this.isShipToPrescriberFromCategory = false;
                this.shippingOptionsTouched = false;
                if (this.order.signatureRequired) this.shippingOptions.push("SignatureRequired");
                if (this.order.overridePriority) this.shippingOptions.push("OverridePriority");
                // Hydrate saturday as persisted; an External-API order may carry it without priority — reconcile only on an explicit user edit.
                if (this.order.saturdayDelivery) this.shippingOptions.push("SaturdayDelivery");
                if (this.order.overrideAddress) this.shippingOptions.push("OverrideAddress");
                if (this.order.disableOneRate) this.shippingOptions.push("DisableOneRate");
                // A persisted ship-to-prescriber election loads locked (fail-closed) so a prescriber-only
                // drug can't be unchecked before the async drug-category lookup runs; checkShipToPrescriber
                // relaxes the lock only on a definitive negative result from that lookup.
                if (this.order.shipToPrescriber) {
                    this.shippingOptions.push("ShipToPrescriber");
                    this.isShipToPrescriberFromCategory = true;
                }

                this.dataPrescriptions = this.order?.prescriptions || new Array<Prescription>();
                this.savedPrescriptionKeys = deliveryLogScriptKeys(this.dataPrescriptions);
                if (this.dataPrescriptions.length == 1) {
                    let script = this.dataPrescriptions[0];
                    const pv2 = {
                        name: "PV2",
                        params: {
                            orderID: response.data.id.toString(),
                            storeID: script.storeID.toString(),
                            rxNumber: script.rxNumber.toString(),
                            rfNumber: (script.rfNumber || 0).toString(),
                        },
                    };

                    if (
                        this.from?.name == "VerificationQueue" &&
                        (script.pV2ID || 0) == 0 &&
                        (script.dispenseErrorId || 0) == 0 &&
                        this.$canNavigateTo(pv2)
                    ) {
                        this.$router.push(pv2);
                    }
                }
                this.buttonTitles.clear = this.order?.id ? "Cancel Order" : "";
                this.getCancelReasonOptionsFromConfiguration();

                if (this.canErrorStatus) {
                    this.buttonTitles.clear = this.order.status == OrderStatus.Error ? "Undo Error Order" : "Error Order";
                }

                this.selectedPayments = this.order.payments;
                const chargeIdList = this.selectedPayments.map((payment) => payment.chargeId);
                if (chargeIdList?.length) {
                    axios
                        .get(`/stripe/charges/${this.routeOrderId}`)
                        .then((response) => {
                            this.selectedStripeCards = response.data;
                            this.updateStripeCardList();
                        })
                        .catch((err) => {
                            const error = err as AxiosError;
                            let errorMsg = "Error while loading payments.";
                            if (error.response?.status === 418) {
                                this.showLockedMessage(
                                    EntityType.Order,
                                    this.routeOrderId?.toString(),
                                    `Order ${this.routeOrderId}`,
                                    error.response.data
                                );
                            } else {
                                this.$notification(NotificationOptions.error(error));
                                this.$notification(
                                    NotificationOptions.errorSaveNotificationPreset(errorMsg, error),
                                );
                            }
                            console.error(errorMsg, {
                                error,
                                response: error?.response,
                            });
                        });
                }
                this.loadPatient();
                this.fetchProgramDrugsFlags();
            })
            .catch((err) => {
                const error = err as AxiosError;
                let errorMsg = "Error while loading order";
                console.error(errorMsg, {
                    error,
                    response: error?.response,
                });
                if (error.response?.status === 418) {
                    this.showLockedMessage(
                        EntityType.Order,
                        this.routeOrderId?.toString(),
                        `Order ${this.routeOrderId}`,
                        error.response.data
                    );
                } else {
                    this.$notification(
                        NotificationOptions.errorSaveNotificationPreset(errorMsg, error),
                    );
                }
            })
            .finally(() => {
                this.fetchAvailableRx();
            });
    }

    loadOrders(patientId?: string | number) {
        patientId = patientId || this.patient.id;
        this.selectedStore = new Store();
        this.selectedStore.id = this.order.storeID;
        this.selectedStore.name = this.order.storeName;
        //Get patient's prescriptions and format the data to be shown in the grid.
        axios
            .get<Order[]>(
                `/Patient/${patientId}/orders/${this.showCancelled}/${this.showShipped}/${this.selectedStore.id}`,
            )
            .then((response) => {
                this.orders = response.data.filter((o) => o.id != this.order.id);
                this.orders.sort((a: Order, b: Order) => {
                    return new Date(b.created).getTime() - new Date(a.created).getTime();
                });
            })
            .catch((err) => {
                const error = err as AxiosError;
                console.error("Problem loading patient orders", {
                    error,
                    response: error?.response,
                });
                if (error.response?.status === 418) {
                    this.showLockedMessage(
                        EntityType.Order,
                        this.order?.id?.toString(),
                        `Order ${this.order?.id}`,
                        error.response.data
                    );
                } else {
                    this.$notification(NotificationOptions.error(error));
                }
            });
    }

    protected mergeOrder: Order = {} as Order;

    patientOrderMerge(item: Order) {
        if (item.storeID != this.order.storeID) {
            this.$notification(NotificationOptions.error("Cannot merge orders with different store locations."));
            return;
        }
        this.mergeOrder = item;
        this.$bvModal.show('merge-modal');

    }

    async completeManualShip() {
        try {
            if (!isNaN(Number(this.shipment.shipmentType))) {
                this.shipment.shipmentType = this.deliveryCodes[Number(this.shipment.shipmentType)];
            }

            await axios.post(`/api/Shipping/Manual/${this.order.id}`, this.shipment);
            this.backToPreviousPage();
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Order,
                    this.order?.id?.toString(),
                    `Order ${this.order?.id}`,
                    error.response.data
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        }
    }

    async completeMerge() {
        const targetOrderID = this.order.id;
        const sourceOrderID = this.mergeOrder.id;
        try {
            const response = await Axios.post<Order>(`/order/merge-orders/${sourceOrderID}/${targetOrderID}`);
            this.order = new Order(response.data);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.mergeOrder = {} as Order;
        this.loadOrders(this.patientId);
        await this.loadOrder();
    }

    @Watch("order.patientAddressId")
    @Watch("patient.address1")
    addressChange() {
        this.shipment.address1 = this.patient.address1;
        this.shipment.address2 = this.patient.address2;
        this.shipment.city = this.patient.addressCity;
        this.shipment.state = this.patient.addressState;
        this.shipment.zip = this.patient.addressZip;

        if (this.order.patientAddressId) {
            const address = this.availableAddresses.find(pa => pa.id === this.order.patientAddressId);
            this.shipment.address1 = address?.address1 ?? "";
            this.shipment.address2 = address?.address2 ?? "";
            this.shipment.city = address?.city ?? "";
            this.shipment.state = address?.state ?? "";
            this.shipment.zip = address?.postalCode ?? "";
        }
    }

    @Watch("routeOrderId")
    routeOrderIdChange(newVal: number, oldVal: number) {
        if (newVal && newVal !== oldVal) {
            this.acquireOrderLock();
            this.loadOrder();
        }
    }

    @Watch("patientId")
    patientIdChange(newVal?: number | string, _oldVal?: number | string) {
        this.loadOrders(newVal);
        // Reload the patient's AR accounts so the tender dropdown is populated. The election itself
        // is owned entirely by loadOrder (it hydrates from the order, or clears when none exists),
        // so it must NOT be reset here — in this view patientId only changes because an order just
        // loaded, and resetting would wipe the election loadOrder just rehydrated.
        if (this.arEnabled) this.loadArAccounts();
    }

    @Watch("selectedStore.id")
    storeIdChange(val: any, oldVal: any) {
        if (val == oldVal) return;

        this.selectedStoreId = val?.toString();
        this.filterAvailableToOrder(undefined, false);
    }

    @Watch("selectedProgram.id")
    selectedProgramIdChange(val: any, oldVal: any) {
        if (val == oldVal) return;

        this.filterAvailableToOrder(undefined, false);
        this.setDefaultDeliveryCode();
    }

    initObject() {
        this.order.patientID = this.patientId;
        this.order.prescriptions = [];
        this.order.items = [];
    }

    async loadPrinters() {
        try {
            const response = await Axios.get<Printer[]>('/Printer');
            this.printers = response.data;
            this.selectedPrinter = JSON.parse(localStorage.getItem("CPHubVerifyPrinter") as string) as Printer ?? this.printers[0];
        } catch (error) {
            console.warn(error);
        }
    }

    loadPatient() {
        if (!this.patientId) return;
        axios
            .get(`/Patient/${this.patientId}`)
            .then((response) => {
                this.patient = Object.assign(new Patient(), response.data);
                this.loadPatientPaymentMethods();
                this.normalizePrimaryAddressSelection();

                if (
                    this.patient.signatureRequired &&
                    !this.shippingOptions.includes("SignatureRequired")
                ) {
                    this.shippingOptions.push("SignatureRequired");
                }
            })
            .catch((error) => {
                console.warn("Error while loading the Patient", {
                    error,
                    response: error?.response,
                });
                this.$notification(NotificationOptions.error(error));
            });
    }

    // The address dropdown's primary option uses value=null, but patient-app self-checkout saves
    // the primary address's real id. Collapse it back to null so the primary shows selected.
    private normalizePrimaryAddressSelection() {
        if (this.order.patientAddressId == null) return;
        const primary = this.patient?.addresses?.find(a => a.isPrimary);
        if (primary && primary.id === this.order.patientAddressId) {
            this.order.patientAddressId = null;
        }
    }

    fetchAvailableRx() {
        if (!this.patientId) return;
        axios
            .get<Prescription[]>(`/api/Prescription/AutoFillAvailableToOrder/${this.patientId}`)
            .then((response) => {
                const scripts = response.data.map((rx) => new Prescription(rx));
                this.unfilteredAvailableToOrder = scripts.concat(
                    this.order.prescriptions,
                );
                this.filterAvailableToOrder();
                if (!this.routeOrderId && this.preselectRxId) {
                    this.attachExplicitRx(this.preselectRxId);
                }
            })
            .catch((err) => {
                this.$notification(NotificationOptions.error(err));
                console.error("Problem while pulling available prescriptions to order", {
                    err,
                    response: err?.response,
                });
            });
    }

    // Mirrors the order-ready SP's hard gates; the soft PV1-pending/unbilled filters are intentionally not re-applied (that's the point of the explicit attach).
    async attachExplicitRx(rxId: string) {
        const existing = this.unfilteredAvailableToOrder.find((p) => p.rxID === rxId);
        if (existing) {
            this.filterAvailableToOrder([existing]);
            return;
        }
        try {
            const resp = await axios.get<Prescription>(`/api/Prescription/${rxId}`);
            const rx = new Prescription(resp.data);

            const orderableStatus = [RefillStatusCode.Refillable, RefillStatusCode.AutoRefillable, RefillStatusCode.NonRefillable, RefillStatusCode.TransferIn];
            if (!orderableStatus.includes(rx.status) || rx.isExpired) {
                this.$notification(NotificationOptions.warning("This prescription is not in an orderable state and was not added.", "New Order"));
                return;
            }
            if (rx.orders?.some((o) => o.status !== OrderStatus.Canceled && o.status !== OrderStatus.Error)) {
                this.$notification(NotificationOptions.warning("This prescription is already on another order and was not added.", "New Order"));
                return;
            }
            if (rx.isCII && await this.isC2OrderingBlocked()) {
                this.$notification(NotificationOptions.warning("Schedule II ordering is disabled for this pharmacy; this prescription was not added.", "New Order"));
                return;
            }

            rx.productNameShort = rx.productNameShort || rx.drugName;
            rx.requiresPV1 = rx.rfNumber === 0 && !((rx.pV1ID ?? 0) > 0);
            this.unfilteredAvailableToOrder.push(rx);
            // Pin store/program so a mid-fetch selection can't filter the row out; await lets the selectedProgram watcher settle before the selecting filter.
            this.selectedStore = Object.assign(new Store(), { id: rx.storeID });
            this.selectedProgram = Object.assign(new Program(), { id: Number(rx.programID) || 0, name: rx.programName });
            await this.$nextTick();
            this.filterAvailableToOrder([rx]);
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    private async isC2OrderingBlocked(): Promise<boolean> {
        if (this.blockC2Cache !== null) return this.blockC2Cache;
        try {
            const resp = await axios.post<Record<string, boolean>>("/api/Configuration/values", ["BlockC2PatientAppOrders"]);
            this.blockC2Cache = !!resp.data.BlockC2PatientAppOrders;
        } catch {
            this.blockC2Cache = true; // fail-closed: a controlled-substance gate must not bypass on a config error
        }
        return this.blockC2Cache;
    }

    async fetchProgramDrugsFlags() {
        const rxWithProgram = this.dataPrescriptions?.find((rx) => rx.programID);

        if (!rxWithProgram?.programID) {
            this.selectedProgram = new Program();
            this.isProgramWithCopayCollectionFlag = false;
            return;
        }

        try {
            const res = await axios.get(`/Program/${rxWithProgram.programID}`, {
                params: {
                    includeFlags: true,
                    includeDrugs: true,
                },
            });

            this.selectedProgram.id = Number(rxWithProgram.programID);
            this.selectedProgram.flags = res.data.flags;
            this.selectedProgram.drugs = res.data.drugs;
            this.selectedProgram.deliveryMethod = res.data.deliveryMethod;

            this.isProgramWithCopayCollectionFlag = !!this.selectedProgram?.flags?.some(
                (f) => f.name.includes("Allow Collected By Partner") && f.value,
            );
        } catch (err) {
            const error = err as any;
            console.error("Error while pulling program drugs and flags", {
                error,
                response: error?.response,
            });
            this.$notification(NotificationOptions.error(err));
        }
    }

    async prescriptionsSelected(selectedRows: Prescription[]) {
        this.dataPrescriptions = selectedRows;

        // set the selected program
        const rx = this.dataPrescriptions?.find((rx) => rx.programID);
        if (rx) {
            this.selectedProgram.id = Number(rx.programID);
            this.selectedProgram.name = rx.programName;
            await this.fetchProgramDrugsFlags();
        } else {
            this.selectedProgram = new Program();
            this.isProgramWithCopayCollectionFlag = false;
        }

        if (selectedRows?.length) this.filterAvailableToOrder(selectedRows);

        this.haveSelectedPrescriptions = selectedRows.length > 0;

        // Check if any selected prescriptions have "Ship To Prescriber" drug category
        await this.checkShipToPrescriber(selectedRows);

        // Reset programmatic select flag and set delivery code
        // The internal guards in setDefaultDeliveryCode() handle edit mode logic
        this.isProgrammaticSelect = false;
        this.setDefaultDeliveryCode();

        this.fetchAcceptanceConfigurations(selectedRows);
    }

    async checkShipToPrescriber(prescriptions: Prescription[]) {
        const wasCategoryDriven = this.isShipToPrescriberFromCategory;

        if (!prescriptions?.length) {
            this.isShipToPrescriberFromCategory = false;
            if (wasCategoryDriven) {
                const idx = this.shippingOptions.indexOf("ShipToPrescriber");
                if (idx > -1) this.shippingOptions.splice(idx, 1);
            }
            return;
        }

        try {
            // Ship-to-prescriber is a drug-category lookup, and a PackageID is only unique within its
            // DrugSource. Group the package ids by source and query each source separately so a NonGSDD
            // or compound drug is never resolved against an unrelated GSDD package's categories.
            const packageIdsBySource = new Map<number, Set<number>>();
            for (const rx of prescriptions) {
                if (!rx.packageID) continue;
                const source = rx.drugSource ?? 0;
                if (!packageIdsBySource.has(source)) packageIdsBySource.set(source, new Set<number>());
                packageIdsBySource.get(source)!.add(rx.packageID);
            }

            let hasShipToPrescriber = false;
            for (const [source, packageIds] of Array.from(packageIdsBySource.entries())) {
                const response = await axios.post<boolean>(
                    '/api/DrugCategory/IsShipToPrescriber',
                    [...packageIds],
                    { params: { drugSource: source } },
                );
                if (response.data) {
                    hasShipToPrescriber = true;
                    break;
                }
            }

            this.isShipToPrescriberFromCategory = hasShipToPrescriber;
            if (hasShipToPrescriber && !this.shippingOptions.includes("ShipToPrescriber")) {
                this.shippingOptions.push("ShipToPrescriber");
            } else if (!hasShipToPrescriber && wasCategoryDriven) {
                const idx = this.shippingOptions.indexOf("ShipToPrescriber");
                if (idx > -1) this.shippingOptions.splice(idx, 1);
            }
        } catch {
            // Silently fail - backend will still enforce during save
        }
    }

    fetchAcceptanceConfigurations(selectedRows: Prescription[]) {
        this.loadingAcceptanceConfigurations = true;
        const config = {
            params: {
                rxIds: selectedRows.map(rx => new Prescription(rx).rxID),
            },
        } as AxiosRequestConfig;
        axios.get<AcceptanceConfiguration[]>("/api/AcceptanceConfiguration/Active", config)
             .then(resp => {
                 const configs = resp.data;
                 this.acceptanceConfigurations = configs.map(ac => {
                     const config = new AcceptanceConfiguration(ac);
                     config.checked = this.order.acceptanceRecords.some(ar => ar.configurationId == config.id);
                     return config;
                 });

             })
             .catch(err => {
                 this.$notification(NotificationOptions.error(err));
             })
             .finally(() => {
                 this.loadingAcceptanceConfigurations = false;
             });
    }

    async openDocument(item: AcceptanceConfiguration) {
        if (!item?.imageId) return;
        try {
            const imageBlob = await axios.get(`/api/Image/${item.imageId}`, { responseType: "blob" });
            const objectUrl = URL.createObjectURL(imageBlob.data);
            window.open(objectUrl, "_blank");
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    filterAvailableToOrder(
        selectedRows: Array<Prescription> = new Array<Prescription>(),
        selectRows = true,
    ) {
        //Set the selected store from selected rx
        if (selectedRows?.length > 0) {
            const selectedRowStoreId = selectedRows[0].storeID;
            if (selectedRowStoreId != this.selectedStore?.id) {
                this.selectedStore = new Store();
                this.selectedStore.id = selectedRows[0].storeID;
            }
        }

        //Filter available Rx in the table based on selected rx and program.
        const storeId = this.selectedStore?.id;
        const programId = this.selectedProgram?.id;
        const routeMode = this.routeMode;
        if (storeId || programId) {
            this.availableToOrder = this.unfilteredAvailableToOrder.filter(
                (itm) => {
                    const storeCheck = itm.storeID == (storeId > 0 ? storeId : itm.storeID);
                    const programCheck = itm.programID == (programId > 0 ? programId : itm.programID);
                    const routeCheck = routeMode == "verify" || routeMode == "pickup";
                    return storeCheck && (programCheck || routeCheck);
                },
            );
        } else {
            this.availableToOrder = this.unfilteredAvailableToOrder;
        }

        if (this.routeMode == "verify" || this.routeMode == "pickup") {
            this.availableToOrder = this.availableToOrder.filter((el) =>
                this.order.prescriptions.includes(el),
            );
        }

        if (this.rxTable) {
            this.$nextTick(() => {
                if (!selectedRows?.length) {
                    if (this.order.prescriptions && selectRows) {
                        this.order.prescriptions.forEach((rx) => {
                            const idx = this.availableToOrder.indexOf(rx);
                            if (idx != -1) {
                                this.isProgrammaticSelect = true;
                                this.rxTable.selectRow(idx);
                            }
                        });
                    }
                } else {
                    selectedRows.forEach((rx) => {
                        const idx = this.availableToOrder.indexOf(rx);
                        if (idx != -1) {
                            this.isProgrammaticSelect = true;
                            this.rxTable.selectRow(idx);
                        }
                    });
                }
            });
        }
    }

    selectPrinter(printer: any) {
        localStorage.setItem("CPHubVerifyPrinter", JSON.stringify(printer));
        this.selectedPrinter = printer;
    }

    loadPatientPaymentMethods() {
        if (!this.patient?.stripeCustomerId) return;
        axios
            .get(`/stripe/cards/${this.patient.stripeCustomerId}`)
            .then((response) => {
                this.stripeCardList = response.data;
            })
            .catch((error) => {
                console.error("Error while getting Payment Cards from Patient", {
                    error,
                    response: error?.response,
                });
                this.$notification(NotificationOptions.error(error));
            });
    }

    newCardAdded(stripeResponse: StripeCard) {
        this.patient.stripeCustomerId = stripeResponse.customerId;
        this.loadPatientPaymentMethods();
        if (this.selectedStripeCards && this.selectedStripeCards.length > 0) {
            this.updateStripeCardList();
        }
        //this.stripeCardList.push(response.data);
    }

    rxsWereChanged(): boolean {
        const selectedRxs = this.dataPrescriptions
                                ?.map((rx) => Object.assign(new Prescription(), rx))
                                .map((rx) => rx.rxID)
                                .sort()
                                .join(",");
        const orderRxs = this.order.prescriptions
                             ?.map((rx) => Object.assign(new Prescription(), rx))
                             .map((rx) => rx.rxID)
                             .sort()
                             .join(",");

        return selectedRxs != orderRxs;
    }

    /**
     * Delivery Code Priority Determination for the order:
     * 1. Drug/Programs inside the selected program
     * 2. Program Delivery Code
     * 3. Prescriptions Delivery Codes.
     */
    setDefaultDeliveryCode() {
        //in edit mode, Automatic determination of delivery code is blocked until rxs are changed.
        if (this.disableDetermineDeliveryCode && this.rxsWereChanged() && this.routeOrderId) {
            this.disableDetermineDeliveryCode = false;
        }

        if (this.disableDetermineDeliveryCode && this.routeOrderId) return;

        const deliveryCodeCandidates: Array<DeliveryCode | null> = [];

        if (this.selectedProgram?.drugs?.length) {
            const programDrugsInOrder = this.selectedProgram.drugs.filter((dr) =>
                this.dataPrescriptions.some((rx) => rx.packageID === dr.packageID && rx.drugSource === dr.drugSource),
            );
            deliveryCodeCandidates.push(...new Set(programDrugsInOrder.map((i) => i.deliveryCode)));
        }

        deliveryCodeCandidates.push(this.selectedProgram?.deliveryMethod ?? null);

        if (this.dataPrescriptions.some((rx) => Object.assign(new Prescription(), rx).isCII)) {
            deliveryCodeCandidates.push(DeliveryCode.TwoDay);
        }

        deliveryCodeCandidates.push(...new Set(this.dataPrescriptions.map((p) => p.deliveryCode)));

        if (this.patient?.defaultDelivery != null) {
            deliveryCodeCandidates.push(this.patient.defaultDelivery as DeliveryCode);
        }

        if (!deliveryCodeCandidates?.length) {
            this.order.deliveryCode = DeliveryCode.Standard;
            return;
        }

        this.order.deliveryCode = DeliveryCodeImportanceRank.find(dc => deliveryCodeCandidates.includes(dc)) ?? DeliveryCode.Standard;
    }

    @Watch("dataOrderItems")
    @Watch("order.patientAddressId")
    updateAddonPricing(addons: Array<OrderOTCItem>) {
        if (addons?.length && this.routeMode != "verify") {
            axios
                .post(`/OTCItem/CalculateOTCTax/${this.patientId}/${this.selectedStoreId}/${this.order.patientAddressId ?? ''}`, addons)
                .then((response) => {
                    this.dataOrderItems.forEach((DOI) => {
                        DOI.tax =
                            response.data.lineItems[
                                response.data.lineItems.findIndex(
                                    (item: any) => item.id === DOI.otcItemId,
                                )
                                ].taxAmount;
                    });
                })
                .catch((error: AxiosError) => {
                    this.$notification(NotificationOptions.error(error));
                    console.error(error.response?.data);
                });
        }
    }

    addSelectedItem(selItem: OTCItem) {
        let isAlreadyAdded = this.dataOrderItems.some((item) => item.otcItemId == selItem.id);

        if (!isAlreadyAdded) {
            const item = {
                orderId: this.order.id as number,
                otcItemId: selItem.id as number,
                quantity: 1,
                otcItemName: selItem.name,
                price: selItem.defaultPrice,
                tax: 0,
            };

            this.dataOrderItems.push(item);
        }
    }

    removeItem(itemId: number) {
        if (itemId) {
            this.dataOrderItems = this.dataOrderItems.filter((item) => item.otcItemId != itemId);
            this.updateAddonPricing(this.dataOrderItems);
        }
    }

    private recalculateSelectedPayments() {
        this.selectedPayments = [] as Payment[];

        if (this.modeOfPayment == "check") {
            let payment: Payment = {
                id: 0,
                orderId: this.order.id as number,
                patientId: this.patientId as number,
                authorizedAmount: this.amountOwed as number,
                chargeId: `check_${this.checkNumber}`,
            } as Payment;
            this.selectedPayments.push(payment);
        } else if (this.modeOfPayment == "cash") {
            let payment: Payment = {
                id: 0,
                orderId: this.order.id as number,
                patientId: this.patientId as number,
                authorizedAmount: this.amountOwed as number,
                chargeId: `cash`,
            } as Payment;
            this.selectedPayments.push(payment);
        } else {
            for (let i = 0; i < this.selectedStripeCards.length; i++) {
                let payment = {
                    id: 0,
                    orderId: this.order.id as number,
                    patientId: this.patientId as number,
                    authorizedAmount: this.selectedStripeCards[i].amount as number,
                    StripeCardId: this.selectedStripeCards[i].id,
                } as Payment;
                this.selectedPayments.push(payment);
            }
        }
    }

    savePickup() {
        if (this.isSubmitting) return;
        let data = this.order;
        data.status = OrderStatus.ReadyForPickup;
        data.storeID = this.selectedStore.id as number;
        data.prescriptions = this.dataPrescriptions;
        data.items = this.dataOrderItems;
        // saveEdit mutates this.order; a token left on it would make the server adopt and drop this status change.
        data.clientRequestId = undefined;

        this.recalculateSelectedPayments();
        data.payments = this.selectedPayments;
        data.stripeCustomerId = this.patient.stripeCustomerId;

        this.isSubmitting = true;
        const pickupHeaders = this.verifiedToken ? { [POSITIVE_ID_TOKEN_HEADER]: this.verifiedToken } : undefined;
        const pickupConfig = pickupHeaders ? { headers: pickupHeaders } : undefined;
        this.verifiedToken = '';
        axios
            .post("/Order/", data, pickupConfig)
            .then((response) => {
                this.order = new Order(response.data);
                return axios
                    .post(`/Shipping/PickedUp/${this.order.id}`)
                    .then(_resp => {
                        this.$notification(NotificationOptions.successSaveNotificationPreset("Pick Up"));
                        this.backToPreviousPage();
                    })
                    .catch((err) => {
                        console.warn("shipping picked up error: ", err);
                        this.$notification(NotificationOptions.error(err));
                        // The /Order/ POST already persisted ReadyForPickup; reload so the page
                        // reflects the server's actual state rather than a stale local order.
                        return this.loadOrder();
                    });
            })
            .catch((error: AxiosError) => {
                this.$notification(NotificationOptions.error(error));
                return this.loadOrder();
            })
            .finally(() => {
                this.isSubmitting = false;
            });

    }

    markShippingOptionsTouched() {
        this.shippingOptionsTouched = true;
    }

    @Watch("shippingOptions")
    enforceSaturdayRequiresPriority() {
        // Only reconcile on a user shipping edit; skip during load so persisted External-API combos survive.
        if (!this.shippingOptionsTouched) return;
        if (this.shippingOptions.includes("SaturdayDelivery") && !this.shippingOptions.includes("OverridePriority")) {
            this.shippingOptions = this.shippingOptions.filter(o => o != "SaturdayDelivery");
        }
    }

    applyShippingOptions(data: Order) {
        data.signatureRequired = this.shippingOptions.includes("SignatureRequired");
        data.overrideAddress = this.shippingOptions.includes("OverrideAddress");
        data.disableOneRate = this.shippingOptions.includes("DisableOneRate");
        data.shipToPrescriber = this.shippingOptions.includes("ShipToPrescriber");
        // Reconcile priority/saturday from the UI only on an explicit user shipping edit; else keep persisted.
        if (this.shippingOptionsTouched) {
            data.overridePriority = this.shippingOptions.includes("OverridePriority");
            data.saturdayDelivery = data.overridePriority && this.shippingOptions.includes("SaturdayDelivery");
        }
    }

    /**
     * Reconciles a create whose POST failed: asks the server whether this submit's token produced an
     * order anyway and, if so, opens it instead of reporting a failure. Returns the adopted order id,
     * or null when nothing was created (or the answer could not be obtained, which must not be
     * mistaken for "created" — the caller then shows the original error).
     *
     * Skipped only for statuses refused ahead of the save. An ordinary validation 400 still probes:
     * the save can throw after the procedure committed, and those two look identical from here.
     */
    private async adoptOrderFromFailedCreate(status?: number): Promise<number | null> {
        const token = this.clientRequestId;
        if (this.order.id || !token || refusedBeforeSave(status)) return null;

        let existingId: number | null;
        try {
            existingId = await this.probeExistingOrder(token);
        } catch (probeError) {
            console.warn("Could not confirm whether the failed save created an order", probeError);
            return null;
        }
        if (existingId === null) return null;

        // Same ordering as the pre-submit adopt: bind the id before navigating, because this page
        // instance survives the route change and would otherwise stay in create mode.
        this.order.id = existingId;
        this.clearDuplicateIntent();
        this.$notification(NotificationOptions.warning(
            `Order ${existingId} was created despite the error. It has been opened — review it before saving again.`));
        this.$router.push({ name: "OrderDetails", params: { orderId: String(existingId) } })
            .catch(() => this.loadOrder());
        return existingId;
    }

    private async probeExistingOrder(token: string): Promise<number | null> {
        const patientId = this.order.patientID ?? this.patientId;
        try {
            const response = await axios.get<{ id: number } | null>("/api/Order/by-client-request",
                                                                    { params: { patientId, clientRequestId: token } });
            return response.status === 204 ? null : response.data?.id ?? null;
        } catch (error) {
            // A 4xx answers the question (unauthorized/unavailable ⇒ we created nothing); only an
            // unreachable server leaves it unknown, and only that may block the submit.
            const status = (error as AxiosError)?.response?.status;
            if (status && status < 500) return null;
            throw error;
        }
    }

    async saveEdit() {
        if (this.isSubmitting) return;
        // Claim the guard before the first await: a second entry resuming past a stale flag mints its
        // own idempotency token, and on a prescription-less order neither server guard would catch it.
        this.isSubmitting = true;

        try {
            await this.performSaveEdit();
        } catch (error) {
            // Nothing else releases a throw raised before the POST goes out: the request's finally
            // never runs, so the guard would stay set and Save would be dead until a reload. Rethrown
            // so the failure still surfaces rather than being swallowed here.
            this.isSubmitting = false;
            this.releaseSaveButton();
            throw error;
        }
    }

    private async performSaveEdit() {
        // Gate before the idempotency token resolves so a prompted-and-abandoned save mints nothing.
        // Cleared first, because the modal's retry re-enters saveEdit and would trip the guard above.
        if (!this.order.id && !this.allowDuplicateOrder && await this.checkDuplicateOpenOrders()) {
            this.isSubmitting = false;
            return;
        }

        let data = this.order;
        data.status = OrderStatus.Created;
        data.storeID = this.selectedStore.id as number;
        data.prescriptions = this.dataPrescriptions;
        data.items = this.dataOrderItems;
        this.recalculateSelectedPayments();
        data.payments = this.selectedPayments;
        data.stripeCustomerId = this.patient.stripeCustomerId;
        this.applyShippingOptions(data);

        // Carry the AR tender election so the server records it as part of the save (only when the
        // election is usable; otherwise send a cleared election). The server re-validates it.
        const arElected = this.arCoveredAmount > 0;
        data.arAccountId = arElected ? this.arSelectedAccountId : null;
        data.arChargeAmount = arElected ? this.arAmount : null;
        data.arOverrideReason = arElected && this.arOverLimit ? this.arOverrideReason : null;

        // Create only: a prior attempt may have committed without us seeing the response, so ask the
        // server before deciding to reuse, re-mint, or stop.
        if (!this.order.id) {
            const decision = await resolveCreateTokenWithProbe(
                this.clientRequestId, this.lastSubmitSignature, orderContentSignature(data),
                token => this.probeExistingOrder(token));

            if (decision.action === "abort") {
                this.isSubmitting = false;
                this.clearDuplicateIntent();
                this.$notification(NotificationOptions.error(
                    "Could not confirm whether the previous submission created an order. Check the patient's orders before retrying."));
                return;
            }

            if (decision.action === "adopt-existing") {
                // Bind the id BEFORE navigating: this page instance is reused across the route change, so
                // leaving it unset would keep the form in create mode and a second Save would re-charge.
                this.order.id = decision.orderId;
                this.isSubmitting = false;
                // The reused instance would otherwise carry a merge target into the adopted order's next save.
                this.clearDuplicateIntent();
                this.$notification(NotificationOptions.warning(
                    `Order ${decision.orderId} was already created by the previous attempt. It has been opened — re-apply your changes and save.`));
                this.$router.push({ name: "OrderDetails", params: { orderId: String(decision.orderId) } })
                    .catch(() => this.loadOrder());
                return;
            }

            this.clientRequestId = decision.token;
            this.lastSubmitSignature = decision.signature;
            data.clientRequestId = decision.token;
        } else {
            data.clientRequestId = undefined;
        }

        this.acceptanceConfigurations.forEach(ac => {
            if (!ac.checked) {
                console.warn("Attempting to save without having checked all acceptance configurations", ac);
                return;
            }
            if (data.acceptanceRecords.some(ar => ar.configurationId == ac.id)) {
                console.info("skip creating a new acceptance record when one already exists for this configuration", ac);
                return;
            }
            let record = new AcceptanceRecord();
            record.configurationId = ac.id;
            record.patientId = this.patientId;
            if (this.order.id) record.orderId = +this.order.id;
            data.acceptanceRecords.push(record);
        });

        const editHeaders = this.verifiedToken ? { [POSITIVE_ID_TOKEN_HEADER]: this.verifiedToken } : undefined;
        const editConfig = editHeaders ? { headers: editHeaders } : undefined;
        this.verifiedToken = '';
        axios
            .post<Order>(`/api/Order?allowDuplicate=${this.allowDuplicateOrder}`, data, editConfig)
            .then(async (response) => {
                if (response.data?.id) {
                    this.order = new Order(response.data);
                    const mergeTarget = this.pendingMergeTargetId;
                    this.pendingMergeTargetId = null;
                    if (response.data.saveWarning) {
                        // Committed but a follow-up step failed — stay to reconcile; never fold a partial save into another order.
                        // The save went through, so refresh the staleness baseline or the Delivery Log link stays disabled.
                        this.savedPrescriptionKeys = deliveryLogScriptKeys(this.dataPrescriptions);
                        this.$notification(NotificationOptions.warning(mergeTarget
                            ? `${response.data.saveWarning} The order was not merged — review it before merging.`
                            : response.data.saveWarning));
                        return;
                    }

                    if (mergeTarget) {
                        try {
                            await OrderService.mergeOrders(Number(this.order.id), mergeTarget);
                            this.$notification(NotificationOptions.success("New order merged into the existing order."));
                        } catch (mergeError) {
                            console.warn("Order created but merge failed", mergeError);
                            const reason = (mergeError as any)?.response?.data ?? "the merge failed";
                            this.$notification(NotificationOptions.warning(
                                `Order ${this.order.id} was created but could not be merged into order ${mergeTarget}: ${reason}. Review order ${this.order.id} — it will be filled separately.`));
                        }
                    } else {
                        this.$notification(NotificationOptions.successSaveNotificationPreset("Order"));
                    }
                    this.backToPreviousPage();
                }
            })
            .catch(async (error: AxiosError) => {
                const conflict = error?.response?.data as any;
                if (error?.response?.status === 409 && conflict?.requiresDuplicateConfirmation) {
                    // Returns before the token reset: this guard rejects pre-persist, so the token stays adoptable.
                    this.duplicateOpenOrders = (conflict.openOrders || []).map((o: any) => new Order(o));
                    this.showDuplicateModal = true;
                    return;
                }
                console.error("Error saving the order", {
                    error,
                    response: error?.response,
                });

                // A failed create may still have committed — a concurrent submit that lost the race
                // fails against the order the winner just created. Ask before clearing the token,
                // which is the only handle on that order, so the user is not told the save failed
                // when it did not. Reloading instead would not help: a create has no order id yet.
                const adoptedId = await this.adoptOrderFromFailedCreate(error?.response?.status);
                if (adoptedId !== null) return;

                if (shouldResetTokenOnError(error?.response?.status)) this.clientRequestId = null;
                this.$notification(NotificationOptions.error(error));
            })
            .finally(() => {
                this.isSubmitting = false;
                this.clearDuplicateIntent();
            });
    }

    async checkDuplicateOpenOrders(): Promise<boolean> {
        try {
            if (!await OrderService.isDuplicateOpenOrderCheckEnabled()) return false;

            const openOrders = await OrderService.getOpenOrders(Number(this.patientId),
                Number(this.selectedStore?.id) || undefined);
            if (!openOrders.length) return false;

            this.duplicateOpenOrders = openOrders;
            this.showDuplicateModal = true;
            return true;
        } catch (error) {
            console.warn("Duplicate open-order check failed; proceeding without prompt", error);
            return false;
        }
    }

    get canMergeOrders(): boolean {
        return this.$permissions.some(p =>
            p.permissionCodeName === "Pharmacists/Verification Queue" ||
            p.permissionCodeName === "Processing/Online Order Queue");
    }

    duplicateCreateAnyway() {
        this.allowDuplicateOrder = true;
        this.pendingMergeTargetId = null;
        this.retryDuplicateSave();
    }

    duplicateMerge(existingOrder: Order) {
        this.allowDuplicateOrder = true;
        this.pendingMergeTargetId = existingOrder.id ? Number(existingOrder.id) : null;
        this.retryDuplicateSave();
    }

    // saveEdit consumes verifiedToken before POSTing, so a retry must re-arm the gate or it 401s.
    private retryDuplicateSave() {
        const gate = this.$refs.positiveIdGate as { requestVerification?: () => void } | undefined;
        if (!this.verifiedToken && gate?.requestVerification) {
            gate.requestVerification();
            return;
        }
        this.saveEdit();
    }

    save() {
        if (this.routeMode == "pickup") return this.savePickup();
        if (this.routeMode == "edit") return this.saveEdit();

        if (this.routeMode == "verify") {
            const data = this.order;
            data.printerAddress = this.selectedPrinter.id ? this.selectedPrinter.ipAddress : null;
            this.applyShippingOptions(data);
            data.hipaaDocsInBox = true; //!this.shippingOptions.includes("HIPAADocsInBox")

            if (!data.overrideAddress) {
                this.isPrinting = true;
                axios.post<AddressValidationResult>("/Shipping/ValidateShippingAddress", data)
                    .then((response) => {
                        if (response.data.addressChanged) {
                            this.addressValidation = response.data;
                            this.showAddressApproval = true;
                            this.isPrinting = false;
                        } else {
                            this.createLabel();
                        }
                    })
                    .catch((error) => {
                        this.isPrinting = false;
                        this.$notification(NotificationOptions.error(error));
                    });
            } else {
                this.createLabel();
            }
        }
    }

    onAddressApproved() {
        this.createLabel();
    }

    onAddressOverridden() {
        this.order.overrideAddress = true;
        if (!this.shippingOptions.includes("OverrideAddress")) {
            this.shippingOptions.push("OverrideAddress");
        }
        this.createLabel();
    }

    private createLabel() {
        const data = this.order;
        this.isPrinting = true;
        // Pass useNewInventory query param if present (from RTS reship flow)
        const useNewInventory = this.$route.query.useNewInventory === 'true';
        const axiosConfigCreate: AxiosRequestConfig = { params: { useNewInventory } };
        axios.post<any>("/Shipping/CreateLabel", data, axiosConfigCreate).then((response) => {
            this.handleCreateLabelSuccess(response);
        }).catch((error: AxiosError) => {
            let errorMsg = "Printing Failed. ";
            let responseMsg = "";

            let errorResponse = error.response?.data as any;
            if (errorResponse.message) {
                responseMsg = errorResponse.message;
            } else if (typeof errorResponse === "string") {
                responseMsg = error.response?.data as string;
            } else if (errorResponse.length && errorResponse[0]) {
                responseMsg = errorResponse.map((err: any) => {
                    let msg = err?.originalAddress?.companyName ? "Pharmacy: " : "Patient: ";
                    msg += err.messages?.map((msg: any) => msg.message).join(", ");
                    return msg;
                }).join(" ");
            }
            if (
                responseMsg.toString().substring(0, 200) ==
                "There are prescriptions on this order that are not verified, if you wish to continue your order will be split"
            ) {
                this.$bvModal.msgBoxConfirm(responseMsg.toString().substring(0, 200), {
                    title: "Printing Failed.",
                    size: "sm",
                    buttonSize: "sm",
                    okVariant: "danger",
                    footerClass: "p-2",
                    hideHeaderClose: false,
                    centered: true,
                }).then((ok) => {
                    // ok == true when ok is clicked
                    // ok == false when cancel is clicked
                    // ok == null when modal is closed without clicking either ok or cancel
                    if (ok) {
                        // resend the print request with the param to split order
                        const useNewInventorySplit = this.$route.query.useNewInventory === 'true';
                        let axiosConfig: AxiosRequestConfig = { params: { shouldSplit: ok, useNewInventory: useNewInventorySplit } };
                        // This still needs to handle it's .then and .catch blocks and such.
                        axios.post<any>("/Shipping/CreateLabel", data, axiosConfig).then((response) => {
                            this.handleCreateLabelSuccess(response);
                        }).catch((err) => {
                            this.$notification(NotificationOptions.error(err));
                        });
                    } else {
                    }
                }).catch((rejected) => {
                    console.warn("catching msgBoxConfirm: ", rejected);
                });
            } else {
                if (responseMsg) {
                    //If we have detailed reasons why it failed, a message box is used
                    this.$bvModal.msgBoxOk(responseMsg.toString().substring(0, 200), {
                        title: errorMsg,
                        size: "sm",
                        buttonSize: "sm",
                        okVariant: "danger",
                        footerClass: "p-2",
                        hideHeaderClose: false,
                        centered: true,
                    });
                } else {
                    this.$notification(NotificationOptions.error(responseMsg));
                }
            }
        }).finally(() => {
            this.isPrinting = false;
        });
    }

    async updateOrderError() {
        if (this.isUpdatingError) return;

        this.isUpdatingError = true;
        try {
            await axios.post(`api/Order/updateError/${this.order.id}`);
            this.backToPreviousPage();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.isUpdatingError = false;
        }
    }

    clearOrCancel() {
        if (this.isUpdatingError) return;

        if (this.canErrorStatus) {
            this.updateOrderError();
            return;
        }

        if (this.order?.id) {
            this.$bvModal.show("cancel-order-modal");
            return;
        }

        // Clear Form
        this.clearAllLocks();
        this.orderLock = null;

        this.order = new Order();
        this.selectedStore = new Store();
        this.dataPrescriptions = [];
        this.dataOrderItems = [];
        this.selectedProgram = new Program();
        this.initObject();
        (this.$refs.rxTable as any).clearSelected();
        this.filterAvailableToOrder(undefined);
        this.isProgramWithCopayCollectionFlag = false;
        this.collectedByPartner = false;
    }

    cancelOrder(e: any) {
        e.preventDefault();

        if (this.cancelReasonOptions?.length && !this.cancelReasonSelection) {
            this.$notification(NotificationOptions.error("You must select a cancellation reason."));
            return;
        }

        this.order.cancellationReason = this.cancelReasonSelection;

        axios
            .post(`/Order/cancel/`, this.order)
            .then((response) => {
                this.order = new Order(response.data);
                if (this.order.status == OrderStatus.Canceled) {
                    // The cancel itself succeeded either way; a warning means a refund did not, and it
                    // already says so, so it replaces the success toast rather than contradicting it.
                    this.$notification(response.data.saveWarning
                        ? NotificationOptions.warning(response.data.saveWarning)
                        : NotificationOptions.notificationOptionsPreset(
                            "Order was cancelled.",
                            NotificationOptions.NotificationTypes.success,
                        ),
                    );
                    this.$bvModal.hide("cancel-order-modal");
                }
            })
            .catch((error) => {
                let errorMsg = "Error while loading order";
                console.error(errorMsg, {
                    error,
                    response: error?.response,
                });
                this.$notification(NotificationOptions.error(error));
            });
    }

    async getCancelReasonOptionsFromConfiguration() {
        try {
            const response = await this.$http.get<Array<String>>("/Configuration/OrderCancellationReasons");
            if (!response.data?.length) return;
            this.cancelReasonOptions = [
                {
                    value: null,
                    text: 'Select a reason',
                },
                ...response.data,
            ];
        } catch {
            // ignore
        }
    }

    backToPreviousPage() {
        let patientId = this.patientId;
        if (this.routeMode == "verify" && this.$route.params.routeMode !== 'pendingError') {
            this.$router.push({
                name: "VerificationQueue",
                params: { storeId: (this.order?.storeID || 0).toString() },
            });
        } else if (this.routeMode == "verify" && this.$route.params.routeMode === 'pendingError') {
            this.$router.push({
                name: "PendingDispenseErrors",
            });
        } else if (patientId) {
            this.$router.push({
                name: "PatientDashboard",
                params: { id: patientId.toString() },
            });
        }
    }

    otcItemLabel(object: OTCItem) {
        let res = "";
        if (object.name) res = `${object.name}`;

        return res;
    }

    savePayments() {
        this.selectedStripeCards = [];
        for (let i = 0; i < this.stripeCardList.length; i++) {
            if (this.stripeCardList[i].amount > 0) {
                this.selectedStripeCards.push(this.stripeCardList[i]);
            }
        }
    }

    get canVoidPayment(): boolean {
        return this.$permissions.some(p => p.permissionCodeName === "Patient Dashboard/Void Payment");
    }

    isFullyRefunded(item: StripeCard): boolean {
        return item.amountCaptured > 0 && item.amountRefunded >= item.amountCaptured;
    }

    async voidPayment(item: StripeCard) {
        const confirm = await this.$bvModal.msgBoxConfirm(
            "Void/refund this payment and remove it from the order? This cannot be undone.",
            {
                title: "Confirm Void/Refund",
                okVariant: "danger",
                okTitle: "Void/Refund",
                centered: true,
            },
        );
        if (!confirm) return;
        if (this.voidingPayment) return;

        this.voidingPayment = true;
        try {
            const response = await axios.delete(
                `/stripe/charges/${this.routeOrderId}/${item.transactionId}`,
            );
            this.selectedStripeCards = response.data;
            this.updateStripeCardList();
            this.$notification(
                NotificationOptions.success("Payment voided/refunded and removed from the order."),
            );
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Order,
                    this.routeOrderId?.toString(),
                    `Order ${this.routeOrderId}`,
                    error.response.data,
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        } finally {
            this.voidingPayment = false;
        }
    }

    // Void a posted AR payment: reverse the order's AR ledger charge, crediting the amount back to
    // the account. The order's balance owed (prescription patient pay + OTC) is untouched — the AR
    // charge was only a tender against it — so after the reload the order shows its original cost
    // with the AR payment removed. Reversal is a financial correction; it does not un-verify or
    // un-ship the order (hidden once shipped, mirroring the card Void/Refund).
    async voidArPayment() {
        const confirm = await this.$bvModal.msgBoxConfirm(
            "Void this on-account (AR) payment and credit the amount back to the account? This cannot be undone.",
            {
                title: "Confirm Void/Refund",
                okVariant: "danger",
                okTitle: "Void/Refund",
                centered: true,
            },
        );
        if (!confirm) return;
        if (this.voidingArPayment) return;

        this.voidingArPayment = true;
        try {
            // PostReversal returns the reversal transaction, or null (200 with empty body) when there
            // was nothing outstanding to reverse — e.g. the ledger was already zeroed by a concurrent
            // void/unverify. Only claim success when a reversal was actually posted.
            const response = await axios.post(
                `/AR/orders/${this.routeOrderId}/reverse`, { reasonCode: "OrderPaymentVoided" });
            if (response.data) {
                this.$notification(
                    NotificationOptions.success("AR payment voided and credited back to the account."),
                );
            } else {
                this.$notification(
                    NotificationOptions.warning("No outstanding AR payment to void; the account was already settled."),
                );
            }
            // Await so the stale arPostedAmount is refreshed before the loading flag releases the
            // button — otherwise a second click could fire another reverse against the old value.
            await this.loadOrder();
        } catch (err) {
            const error = err as AxiosError;
            if (error.response?.status === 418) {
                this.showLockedMessage(
                    EntityType.Order,
                    this.routeOrderId?.toString(),
                    `Order ${this.routeOrderId}`,
                    error.response.data,
                );
            } else {
                this.$notification(NotificationOptions.error(error));
            }
        } finally {
            this.voidingArPayment = false;
        }
    }

    getVerifyActionButtonsClass(rxRow: Prescription) {
        return {
            "text-info": (rxRow.pV2ID || 0) == 0,
            "text-success": (rxRow.pV2ID || 0) > 0,
            "text-warning": (rxRow.dispenseErrorId || 0) > 0,
        };
    }

    getRowClass(rxRow: Prescription) {
        if (!this.disableFields) return "";

        rxRow = new Prescription(rxRow);
        const isInOrder = this.order?.prescriptions
                              .map((rx) => new Prescription(rx))
                              .map((rx) => rx.rxID)
                              .some((id) => id == rxRow.rxID);
        return isInOrder ? "table-success" : "";
    }

    acceptanceClass(ac: AcceptanceConfiguration): string {
        if (ac.requiresSignature) return "text-danger font-weight-bold";
        return "";
    }

    getPrescriptionVerificationRoute(rxRow: Prescription) {
        return {
            name: "PV2",
            params: {
                orderID: this.order.id,
                storeID: rxRow.storeID,
                rxNumber: rxRow.rxNumber,
                rfNumber: rxRow.rfNumber || 0,
            },
        };
    }

    // Names both what was done and what it silently caused: the operator who pulled the label knows
    // the action, but whoever is looking at a short delivery log later does not.
    removedFromPrintQueueTooltip(rxRow: Prescription): string {
        const who = rxRow.removedFromPrintQueueBy;
        const when = rxRow.removedFromPrintQueue
            ? moment(rxRow.removedFromPrintQueue).format("L LT")
            : null;
        const by = [who && `by ${who}`, when && `on ${when}`].filter(Boolean).join(" ");
        // Tense-neutral: the badge also renders on shipped and canceled orders, where "will not ship"
        // would be false — the line was already excluded rather than about to be.
        return `Pulled from the print queue without printing a label${by ? ` ${by}` : ""}. `
             + "This line is excluded from shipping and from the delivery log.";
    }

    tooltipText(rxRow: Prescription) {
        let text: string;
        if ((rxRow.dispenseErrorId || 0) > 0) {
            text = "Error reported";
        } else if ((rxRow.pV2ID || 0) > 0) {
            text = "Verified By " + rxRow.pv2By;
        } else {
            text = "Verify";
        }

        return text;
    }

    updateStripeCardList() {
        for (let i = 0; i < this.selectedStripeCards.length; i++) {
            const hasRefund = !!this.selectedStripeCards[i].amountRefunded;

            const selectedCardIndex = this.stripeCardList.findIndex(
                (item) => item.id == this.selectedStripeCards[i].id,
            );

            if (selectedCardIndex < 0) continue;

            if (!hasRefund) {
                this.stripeCardList[selectedCardIndex].amount = this.selectedStripeCards[i].amount;
            }

            this.stripeCardList[selectedCardIndex].pendingDelete = false;
        }
    }

    get stripeCardListGetter(): StripeCard[] {
        const ret = this.stripeCardList.filter((c) => !c.pendingDelete);
        return ret;
    }

    async textAcceptanceConfiguration(_item: AcceptanceConfiguration) {
        try {
            const firstName = this.patient.firstName || '';
            const lastName = this.patient.lastName || '';
            const patientName = `${firstName} ${lastName}`.trim() || 'this patient';
            const confirm = await this.$bvModal.msgBoxConfirm(
                `Send NPC text message to ${patientName}?`,
                {
                    title: 'Confirm Send Text Message',
                    okVariant: 'primary',
                    centered: true,
                    okTitle: "Send",
                },
            );
            if (!confirm) return;

            const endpoint = `/api/patient/sendNpcText`;

            await axios.post(endpoint, this.patient);

            this.$notification(NotificationOptions.success("Message Sent"));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    handlePromisedTimeCheck(checked: any) {
        const hourFromNow = moment().add(1, "hours");
        const currentMinutes = hourFromNow.minutes();
        const minutesToAdd = 15 - (currentMinutes % 15);
        const roundedDate = hourFromNow.add(minutesToAdd, 'minutes');

        this.order.promisedDate = (checked ? roundedDate.toDate() : undefined);
    }

    async handlePromisedDateChange(value: any) {
        this.order.promisedDate = value;

        await this.$nextTick();

        if (moment(this.order.promisedDate).isBefore()) {
            this.$notification(
                NotificationOptions.notificationOptionsPreset(
                    "Promised time cannot be in the past",
                    NotificationOptions.NotificationTypes.warning,
                ),
            );
            this.handlePromisedTimeCheck(true);
        }
    }

    get availableToOrderRequirePV1() {
        return this.unfilteredAvailableToOrder?.filter(i => i.isPV1Required || i.requiresPV1);
    }

    prettyPrinterName(printer: any) {
        if (!printer) return "No Printer Defined";
        if (!printer.serverName) return printer.name;
        return printer.name.replace(printer.serverName, "").replace(/\\/g, "");
    }

    protected isLoadingPrinters = true;

    get hasLocalPrinters() {
        return !!this.localPrinters.length;
    }

    async getLocalPrinters() {
        try {
            this.isLoadingPrinters = true;

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                // Request printers through the shared worker
                const printers = await new Promise<any[]>((resolve) => {
                    const timeout = setTimeout(() => {
                        console.log("WebSocket printer request timed out, falling back to HTTP");
                        resolve([]);
                    }, 3000);

                    const handleMessage = (e: MessageEvent) => {
                        if (e.data[0] === "clientServicePrinters") {
                            clearTimeout(timeout);
                            workerPort.removeEventListener("message", handleMessage);
                            resolve(e.data[1] || []);
                        }
                    };

                    workerPort.addEventListener("message", handleMessage);
                    workerPort.postMessage(["clientServiceRefreshPrinters", null]);
                });

                if (printers.length > 0) {
                    this.localPrinters = printers;
                    return;
                }
            }

            // Fallback to HTTP if WebSocket not available or no printers received
            const response = await this.$http.get("http://localhost:7274/print/devices");
            this.localPrinters = response.data;
        } catch {
            //ignore
        } finally {
            this.isLoadingPrinters = false;
        }
    }

    private handleCreateLabelSuccess(response: any) {
        const printWarning: string | undefined = response.data?.printWarning;
        this.printLocalRaw(response.data.zplEntries).then(() => {
            if (printWarning) {
                // Order is verified, but server-side printing failed or fell back to local — surface it so staff can reprint/confirm.
                this.$notification(NotificationOptions.warning(printWarning, "Label Printing Warning"));
            } else {
                this.$notification(NotificationOptions.notificationOptionsPreset("Printed!", NotificationOptions.NotificationTypes.success));
            }
            this.backToPreviousPage();
        }).catch((err) => {
            this.$notification(NotificationOptions.error(err));
        });
    }

    async printLocalRaw(zplEntries?: string[]) {
        if (!zplEntries?.length) return;

        try {
            const rawPrintRequest = {
                printerName: this.selectedPrinter.name,
                rawContents: zplEntries,
                documentName: "ship-zpl",
                copies: 1,
            };

            // Try to use WebSocket connection via shared worker if available
            const workerPort = (this as any).$workerPort;
            if (workerPort) {
                workerPort.postMessage(["clientServicePrintRaw", rawPrintRequest]);
            } else {
                // Fallback to HTTP if WebSocket not available
                const printerEndpoint = "http://localhost:7274/print/raw";
                await Axios.post(printerEndpoint, rawPrintRequest);
            }
        } catch (error) {
            console.error('Error printing labels:', error);
            throw error;
        }
    }

    scrollToOpenOrders() {
        this.openOrdersCard?.$el.scrollIntoView({ behavior: "smooth" });
    }

    @Ref("notesList") protected notesList!: any;

    refreshNotes() {
        this.notesList?.loadNotes();
    }

    blockingTaskFilter(task: UserTask) {
        if (task.assignments?.find(a => a.patient?.id === this.patient.id)) return true;
        if (task.patients?.find(p => p.id === this.patient.id)) return true;
        if (task.prescriptions?.find(rx => this.dataPrescriptions.find(dp => dp.storeID === rx.storeID && dp.rxNumber === rx.rxNumber))) return true;
        return false;
    }

}
</script>

<style scoped lang="scss">
.fade-enter-active,
.fade-leave-active {
    transition: opacity .2s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
}

::v-deep(.dropdown-item.active) {
    background-color: rgba(0, 0, 255, 0.05);
    font-weight: bold;
}

// The Now UI theme renders `.form-control` with a transparent background, so the
// over-limit reason input took on the warning alert's yellow, leaving its light
// placeholder text unreadable. Force a solid white field for legible contrast.
::v-deep(.ar-override-reason) {
    background-color: #fff;
}

// The theme's default `.form-control` placeholder color ($medium-gray, #DDDDDD)
// is nearly invisible on white. Darken it to a $gray-600 that clears WCAG AA
// (~4.7:1 on white) while still reading as placeholder text.
::v-deep(.ar-override-reason::placeholder) {
    color: #6c757d;
    opacity: 1;
}

.weather-info {
    padding: 1rem;
    display: flex;
    gap: 1.75rem;

    > * {
        display: flex;
        gap: 10px;
        align-items: center;
    }

    .temp {
        font-size: 2.0em;
        font-weight: bold;
        line-height: 1;
    }

    i {
        font-size: 4em;
    }

    small {
        text-transform: uppercase;
        font-weight: bold !important;
        line-height: 1;
        display: inline-block;
        letter-spacing: .5px;
    }

    sub {
        font-size: .45em;
        margin-left: 2px;
        display: inline-block;
        position: relative;
        top: -10px;
        font-weight: 500;
        letter-spacing: .6px;
    }
}
</style>
